import React, { useCallback, useState, useEffect, useLayoutEffect, useRef } from 'react';
import DisclaimerBanner from '../DisclaimerBanner';
import { startSession, withCreds } from '../../authService';
import { createConversationThread, listConversationThreads,
  deleteConversationThread, listArchivedConversationThreads, loadConversationMessages,
  permanentlyDeleteConversationThread, restoreConversationThread,
  selectConversationToResume } from '../../lib/conversationThreads';
import { sendMessage } from '../../lib/sendMessage';
import {
  cancelBrightResolveVoice,
  isAppleMobileVoiceRuntime,
  pauseBrightResolveVoice,
  primeBrightResolveAudio,
  requiresExclusiveVoicePlayback,
  setBrightResolveAudioSessionType,
  resumeBrightResolveVoice,
  speakWithBrightResolveVoice,
  stopBrightResolveVoice,
  unlockBrightResolveAudio,
} from '../../lib/tts';
import {
  createEphemeralVoiceObservationQueue,
  classifyOrdinaryVoiceTranscript,
  classifyThinkingTurnReplacement,
  decideVoiceBargeIn,
  evaluateRecordedVoiceActivity,
  isControlOnlyVoiceCommand,
  observeEphemeralVoiceCandidate,
  recognitionResultSnapshot,
  rearmBargeInRecovery,
  shouldIgnoreRecentAssistantEcho,
  shouldPresentInterruptionCandidate,
} from '../../lib/voiceInterruption';
import {
  ASSISTANT_ECHO_COOLDOWN_MS,
  BARGE_IN_ACOUSTIC_COOLDOWN_MS,
  BARGE_IN_ACOUSTIC_RECOVERY_MS,
  BARGE_IN_FINAL_GRACE_MS,
  BARGE_IN_FALSE_ALARM_RECOVERY_MS,
  LIVE_FINAL_DEBOUNCE_MS,
  LIVE_INTERIM_STALL_MS,
  LIVE_REQUEST_TIMEOUT_MS,
  LIVE_RESPONSE_MAX_TOKENS,
  VOICE_MAX_UTTERANCE_MS,
  voiceTurnFinalizationDelay,
} from '../../lib/liveVoiceConfig';
import { measureVoiceEnergy } from '../../lib/voiceEnergyAnalysis';
import { publishVoiceInputEnergy } from '../../lib/voiceEnergyBus';
import { createAnalyserFrameScheduler } from '../../lib/voiceAnimationFrames';
import { markLoginLatency, reportLoginLatencyTrace } from '../../lib/loginLatency';
import {
  abortVoiceTurn,
  advanceVoiceGeneration,
  beginAbortableVoiceTurn,
  createVoiceSessionController,
  createVoiceProgressWatchdog,
  createVoiceUtteranceGuard,
  captureVoiceTurnOwner,
  finalizedVoiceTranscript,
  isCurrentVoiceGeneration,
  isCurrentVoiceTurnOwner,
} from '../../lib/voiceTurnController';
import { createVoiceLatencyTracker, parseTtsServerTimingHeader } from '../../lib/voiceLatency';
import {
  appliedAudioConstraint,
  createVoiceIncidentRef,
  emitVoiceIncident,
  summarizeVoiceIncidentEvidence,
  voiceBrowserFamily,
  voiceBrowserMetadata,
} from '../../lib/voiceIncidentTelemetry';
import { createRecordedVoiceCapture } from '../../lib/recordedVoiceCapture';
import { isNearConversationBottom } from '../../lib/chatScrollContinuity';
import {
  createFrontendDeliveryTracker,
  createPlaybackRef,
} from '../../lib/frontendDeliveryEvidence';
import {
  classifyVoiceCanaryReadiness,
  voiceCanaryBindingMatches,
  voiceCanaryReadinessMatchesBinding,
} from '../../lib/voiceCanaryBinding';

import TranscriptPanel from './TranscriptPanel';
import TextComposer from './TextComposer';
import VoiceDraftPanel from './VoiceDraftPanel';
import LiveVoicePanel from './LiveVoicePanel';
import ConversationSidebar from './ConversationSidebar';

const genClientMessageId = () =>
  globalThis.crypto && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `cmid-${Date.now()}-${Math.random().toString(16).slice(2)}`;

function clearBrightResolveStorage() {
  try {
    const keys = Object.keys(localStorage);
    for (const k of keys) {
      if (k.startsWith('br_') || k.startsWith('brightresolve_')) {
        localStorage.removeItem(k);
      }
    }
  } catch (_) {}
}

function getSpeechRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function supportsDuplexVoiceMonitoring() {
  if (getSpeechRecognitionCtor()) return true;
  return Boolean(
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder === 'function' &&
    navigator.mediaDevices?.getUserMedia
  );
}

function getDuplexVoiceMonitoringMode() {
  const recordedMonitoringAvailable = Boolean(
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder === 'function' &&
    navigator.mediaDevices?.getUserMedia
  );
  // Apple mobile must release microphone ownership while Sonny speaks. A
  // recognizer restarted during playback can reclaim the communications route
  // and leave a valid ElevenLabs response inaudible.
  if (isAppleMobileVoiceRuntime()) return null;
  // Use one recognition path across capable desktop and mobile browsers.
  // Web Speech exposes interim text while the person is still talking; the
  // recorder remains a bounded fallback for browsers/services that do not
  // provide recognition or report a recoverable recognition failure.
  if (getSpeechRecognitionCtor()) return 'web_speech';
  if (recordedMonitoringAvailable) return 'recorded';
  return null;
}

function reportRecordedVoiceStage(stage, detail = {}) {
  // Diagnostic fields are intentionally bounded operational metadata. Never
  // attach recorded audio, transcripts, prompts, or provider error details.
  console.info('[BRIGHTRESOLVE VOICE CAPTURE]', {
    surface: 'public',
    stage,
    elapsed_ms: Number(detail.elapsed_ms || 0),
    mode: 'recorded',
    reason: String(detail.reason || '').slice(0, 48),
  });
}

export default function ChatShell() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [isSending, setIsSending] = useState(false);

  const [isTranscribeOpen, setIsTranscribeOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const [isLiveOpen, setIsLiveOpen] = useState(false);
  const [liveVoiceState, commitLiveVoiceState] = useState('idle');
  const [ccEnabled, setCCEnabled] = useState(true);
  const [liveTranscript, commitLiveTranscript] = useState('');
  const [assistantPreview, commitAssistantPreview] = useState('');

  const [playingMessageId, setPlayingMessageId] = useState(null);
  const [threads, setThreads] = useState([]);
  const [archivedThreads, setArchivedThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [isThreadLoading, setIsThreadLoading] = useState(true);
  const [isConversationPanelOpen, setIsConversationPanelOpen] = useState(false);
  const [isHistorySavingEnabled, setIsHistorySavingEnabled] = useState(null);
  const [orbSpeechActive, setOrbSpeechActive] = useState(false);

  const messagesEndRef = useRef(null);
  const transcriptRef = useRef(null);
  const shouldFollowMessagesRef = useRef(true);
  const forceConversationBottomRef = useRef(false);
  const sessionInitRef = useRef(null);
  const sessionIdRef = useRef(null);
  const activeThreadIdRef = useRef(null);
  const recognitionRef = useRef(null);
  const recorderTimerRef = useRef(null);
  const recorderDeadlineRef = useRef(null);
  const currentUtteranceRef = useRef(null);

  const mediaStreamRef = useRef(null);
  const liveInputTrackCleanupRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const analyserFrameSchedulerRef = useRef(null);
  const sourceRef = useRef(null);
  const micPermissionDeniedRef = useRef(false);

  const transcribeKeepAliveRef = useRef(false);
  const transcribeFinalBufferRef = useRef('');
  const liveFinalBufferRef = useRef('');
  const liveSilenceTimerRef = useRef(null);
  const liveFinalTranscriptRecoveryRef = useRef(null);
  const bargeInTimerRef = useRef(null);
  const bargeInActiveRef = useRef(false);
  const bargeInInterruptedRef = useRef(false);
  const bargeInPausedRef = useRef(false);
  const bargeInPendingRef = useRef(null);
  const bargeInCycleOpenRef = useRef(false);
  const bargeInCycleGenerationRef = useRef(0);
  const bargeInOwnerRef = useRef(null);
  const bargeInSpeechActiveRef = useRef(false);
  const bargeInIgnoreResultsUntilRef = useRef(0);
  const bargeInDecisionTimerRef = useRef(null);
  const bargeInActivityTimerRef = useRef(null);
  const bargeInAcousticCooldownUntilRef = useRef(0);
  const voiceIncidentRef = useRef('');
  const voiceLifecycleRef = useRef('');
  const voiceThreadOwnerRef = useRef('');
  const voiceMicrophoneEverAcquiredRef = useRef(false);
  const voicePreviousThreadRef = useRef('');
  const latestMicrophoneEnergyRef = useRef(0);
  const appliedAudioConstraintsRef = useRef({});
  const recordedBargeInRecorderRef = useRef(null);
  const recordedBargeInDiscardRef = useRef(false);
  const recordedBargeInSequenceRef = useRef(0);
  const isLiveOpenRef = useRef(false);
  const isMutedRef = useRef(false);
  const liveVoiceStateRef = useRef('idle');
  // Every live session owns a generation. Closing, muting, or reopening the
  // panel invalidates older network/audio callbacks before they can speak.
  const liveVoiceGenerationRef = useRef(0);
  const voiceSessionIdRef = useRef('');
  const liveRequestAbortRef = useRef(null);
  const livePendingGenerationRef = useRef(null);
  const pendingLiveUserTranscriptRef = useRef('');
  const thinkingInterruptionSequenceRef = useRef(0);
  const thinkingInterruptionOwnerRef = useRef(null);
  const liveInputSuspendedRef = useRef(false);
  const liveProgressWatchdogRef = useRef(null);
  const recentAssistantSpeechRef = useRef({ text: '', endedAt: 0 });
  const recognitionGenerationRef = useRef(0);
  const voiceUserTurnSequenceRef = useRef(0);
  const activeVoiceUserTurnIdRef = useRef(0);
  const voiceSessionOpeningRef = useRef(null);
  const voiceCanaryBindingRef = useRef(null);
  const pendingDisplayEvidenceRef = useRef(new Map());
  const activeDeliveryTrackerRef = useRef(null);
  const activePlaybackEvidenceRef = useRef('');
  const voiceLatencyRef = useRef(createVoiceLatencyTracker({
    surface: 'public',
    ownership: () => ({
      conversationId: activeThreadIdRef.current,
      voiceSessionId: voiceSessionIdRef.current,
      generationId: liveVoiceGenerationRef.current,
      recognitionGenerationId: recognitionGenerationRef.current,
      userTurnId: activeVoiceUserTurnIdRef.current,
      aiGenerationId: livePendingGenerationRef.current ?? liveVoiceGenerationRef.current,
    }),
  }));
  const voiceObservationQueueRef = useRef(createEphemeralVoiceObservationQueue());
  const voiceSessionControllerRef = useRef(null);
  if (voiceSessionControllerRef.current == null) {
    voiceSessionControllerRef.current = createVoiceSessionController({
      initialState: 'idle',
      onSnapshot: (snapshot) => {
        liveVoiceStateRef.current = snapshot.state;
        commitLiveVoiceState(snapshot.state);
        commitLiveTranscript(snapshot.userTranscript);
        commitAssistantPreview(snapshot.assistantText);
      },
      onInvalidTransition: ({ currentState, nextState }) => {
        console.warn('[BRIGHTRESOLVE VOICE] Rejected invalid state transition', {
          surface: 'public',
          current_state: currentState,
          next_state: nextState,
        });
      },
    });
  }
  if (liveProgressWatchdogRef.current == null) {
    liveProgressWatchdogRef.current = createVoiceProgressWatchdog({
      timeoutMs: 2500,
      onTimeout: (owner) => {
        if (
          !isLiveOpenRef.current ||
          isMutedRef.current ||
          owner?.generationId !== liveVoiceGenerationRef.current ||
          owner?.voiceSessionId !== voiceSessionIdRef.current ||
          owner?.conversationId !== activeThreadIdRef.current
        ) return;
        voiceLatencyRef.current.mark('state_watchdog_triggered');
        abortVoiceTurn(liveRequestAbortRef);
        advanceVoiceGeneration(liveVoiceGenerationRef);
        livePendingGenerationRef.current = null;
        pendingLiveUserTranscriptRef.current = '';
        setIsSending(false);
        setLiveTranscript('');
        setLiveVoiceState('listening');
        liveVoiceStateRef.current = 'listening';
        restartLiveRecognition();
      },
    });
  }
  const setLiveVoiceState = (nextState, changes = {}) => (
    voiceSessionControllerRef.current.transition(nextState, changes)
  );
  const setLiveTranscript = (value) => (
    voiceSessionControllerRef.current.setUserTranscript(value)
  );
  const setAssistantPreview = (value) => (
    voiceSessionControllerRef.current.setAssistantText(value)
  );
  const requestVoiceCanaryReadiness = async (threadId) => {
    try {
      const response = await withCreds('/api/canary/cq3-voice-listen/readiness', {
        method: 'POST',
        body: JSON.stringify({ thread_id: threadId, voice_mode: true }),
      });
      const payload = await response.json().catch(() => null);
      return classifyVoiceCanaryReadiness({
        responseOk: response.ok,
        statusCode: response.status,
        payload,
        threadId,
      });
    } catch (_) {
      return { kind: 'blocked', reason: 'readiness_request_failed' };
    }
  };
  const rejectVoiceCanaryBinding = () => {
    voiceCanaryBindingRef.current = null;
    isLiveOpenRef.current = false;
    liveInputSuspendedRef.current = false;
    abortVoiceTurn(liveRequestAbortRef);
    advanceVoiceGeneration(liveVoiceGenerationRef);
    livePendingGenerationRef.current = null;
    pendingLiveUserTranscriptRef.current = '';
    liveProgressWatchdogRef.current?.clear?.();
    stopRecognition();
    releaseLiveInputAudio();
    setIsRecording(false);
    setIsSending(false);
    setIsLiveOpen(false);
    setLiveTranscript('');
    setAssistantPreview('');
    setLiveVoiceState('error');
    liveVoiceStateRef.current = 'error';
    voiceSessionIdRef.current = '';
    voiceSessionOpeningRef.current = null;
    setError('Live Voice cannot start until the assigned canary conversation is open.');
  };
  const traceVoiceLifecycle = (stage, details = {}) => {
    const metric = voiceLatencyRef.current.mark(stage);
    if (
      typeof window !== 'undefined' &&
      (process.env.NODE_ENV !== 'production' || window.__BR_VOICE_TRACE__ === true)
    ) {
      console.debug('[BRIGHTRESOLVE VOICE TRACE]', {
        stage,
        voice_session_id: voiceSessionIdRef.current,
        conversation_id: activeThreadIdRef.current,
        recognition_generation_id: recognitionGenerationRef.current,
        user_turn_id: activeVoiceUserTurnIdRef.current,
        ai_generation_id: livePendingGenerationRef.current ?? liveVoiceGenerationRef.current,
        ...details,
      });
    }
    return metric;
  };
  const markThinkingVisible = useCallback(() => {
    voiceLatencyRef.current.mark('thinking_ui_painted');
  }, []);
  const traceVoiceIncident = (stage, details = {}) => {
    const incidentRef = voiceIncidentRef.current;
    if (!incidentRef) return false;
    return emitVoiceIncident({
      stage,
      incident_ref: incidentRef,
      browser_family: voiceBrowserFamily(),
      monitoring_mode: getDuplexVoiceMonitoringMode() || 'none',
      recognition_generation: recognitionGenerationRef.current,
      ai_generation: livePendingGenerationRef.current ?? liveVoiceGenerationRef.current,
      candidate_sequence: bargeInCycleGenerationRef.current,
      ...appliedAudioConstraintsRef.current,
      voice_session_ref: voiceLifecycleRef.current,
      thread_owner_ref: voiceThreadOwnerRef.current,
      ...details,
    });
  };
  const traceVoiceLifecycleIncident = (stage, details = {}) => {
    const incidentRef = voiceLifecycleRef.current;
    if (!incidentRef) return false;
    return emitVoiceIncident({
      stage,
      incident_ref: incidentRef,
      voice_session_ref: incidentRef,
      thread_owner_ref: voiceThreadOwnerRef.current,
      browser_family: voiceBrowserFamily(),
      monitoring_mode: getDuplexVoiceMonitoringMode() || 'none',
      recognition_generation: recognitionGenerationRef.current,
      ai_generation: livePendingGenerationRef.current ?? liveVoiceGenerationRef.current,
      owner_valid: isLiveOpenRef.current,
      ...appliedAudioConstraintsRef.current,
      ...details,
    });
  };

  const scrollConversationToBottom = (behavior = 'smooth') => {
    const applyScroll = () => {
      const transcript = transcriptRef.current;
      if (transcript?.scrollTo) {
        // scrollIntoView stops before the container's bottom padding, leaving
        // restored chats visibly close to—but not actually at—their end.
        // WebKit can ignore an object-form `scrollTo({ behavior: 'auto' })`
        // during the first restored-history layout. Direct assignment is the
        // cross-engine synchronous contract for an intentional restore; keep
        // smooth scrolling for subsequent follow-along updates.
        if (behavior === 'auto') {
          // The transcript carries `scroll-behavior: smooth` for ordinary
          // follow-along updates. WebKit applies that CSS behavior even to a
          // direct scrollTop assignment, so a restored conversation can stay
          // at the top while its first layout settles. Override the behavior
          // only for this bounded restore. Settle against the observable bottom
          // state: mobile WebKit may finish the transcript layout several
          // frames after React commits a restored history.
          const priorInlineBehavior = transcript.style.scrollBehavior;
          transcript.style.scrollBehavior = 'auto';
          let remainingFrames = 12;
          let stableBottomFrames = 0;
          const settleRestoredConversation = () => {
            if (!transcript.isConnected || transcriptRef.current !== transcript) {
              transcript.style.scrollBehavior = priorInlineBehavior;
              return;
            }
            messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
            transcript.scrollTop = transcript.scrollHeight;
            const bottomDistance = Math.abs(
              transcript.scrollHeight - transcript.scrollTop - transcript.clientHeight,
            );
            stableBottomFrames = bottomDistance <= 1 ? stableBottomFrames + 1 : 0;
            remainingFrames -= 1;
            if (stableBottomFrames < 2 && remainingFrames > 0) {
              window.requestAnimationFrame(settleRestoredConversation);
              return;
            }
            transcript.style.scrollBehavior = priorInlineBehavior;
          };
          settleRestoredConversation();
        } else transcript.scrollTo({ top: transcript.scrollHeight, behavior });
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
      }
      shouldFollowMessagesRef.current = true;
    };
    // A restored transcript must be positioned before the first visible
    // paint. Ordinary follow-along scrolling remains deferred so the latest
    // message can finish rendering before the smooth motion begins.
    if (behavior === 'auto') applyScroll();
    else window.requestAnimationFrame(() => window.requestAnimationFrame(applyScroll));
  };

  const handleTranscriptScroll = () => {
    shouldFollowMessagesRef.current = isNearConversationBottom(transcriptRef.current);
  };

  useLayoutEffect(() => {
    // Loading messages and releasing the thread-loading state can render in
    // separate commits. Keep the one-shot restore authority intact until the
    // transcript has its final flex layout; otherwise mobile WebKit can see a
    // temporarily non-scrollable viewport, consume the flag, then grow at the
    // top when loading completes.
    if (isThreadLoading) return;
    const shouldForce = forceConversationBottomRef.current;
    forceConversationBottomRef.current = false;
    if (shouldForce || shouldFollowMessagesRef.current) {
      scrollConversationToBottom(shouldForce ? 'auto' : 'smooth');
    }
  }, [messages, isThreadLoading]);

  useEffect(() => {
    if (!pendingDisplayEvidenceRef.current.size) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const root = transcriptRef.current;
      if (!root?.isConnected) return;
      const nodes = Array.from(root.querySelectorAll('[data-role="assistant"][data-message-id]'));
      for (const [responseRef, pending] of pendingDisplayEvidenceRef.current) {
        const visibleOwner = activeThreadIdRef.current === pending.conversationRef;
        const committed = visibleOwner && nodes.some(
          (node) => node.dataset.messageId === responseRef && node.isConnected,
        );
        if (!committed) continue;
        pending.tracker.emit('display_committed');
        pendingDisplayEvidenceRef.current.delete(responseRef);
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [messages, activeThreadId]);

  useEffect(() => {
    isLiveOpenRef.current = isLiveOpen;
  }, [isLiveOpen]);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  useEffect(() => {
    liveVoiceStateRef.current = liveVoiceState;
  }, [liveVoiceState]);

  useEffect(() => {
    for (const [responseRef, pending] of pendingDisplayEvidenceRef.current) {
      if (pending.conversationRef === activeThreadId) continue;
      pending.tracker.invalidate();
      pendingDisplayEvidenceRef.current.delete(responseRef);
    }
  }, [activeThreadId]);

  useEffect(() => {
    if (!voiceLifecycleRef.current || !isLiveOpenRef.current) return;
    const previous = voicePreviousThreadRef.current;
    if (previous && activeThreadId && previous !== activeThreadId) {
      activeDeliveryTrackerRef.current?.invalidate?.();
      activeDeliveryTrackerRef.current = null;
      activePlaybackEvidenceRef.current = '';
      traceVoiceLifecycleIncident('route_thread_invalidated', {
        stale_callback: true,
        invalidation_reason: 'thread_owner_changed',
        owner_valid: false,
      });
      voiceThreadOwnerRef.current = createVoiceIncidentRef();
    }
    if (
      voiceCanaryBindingRef.current &&
      !voiceCanaryBindingMatches(voiceCanaryBindingRef.current, activeThreadId)
    ) {
      rejectVoiceCanaryBinding();
    }
    voicePreviousThreadRef.current = activeThreadId || '';
  }, [activeThreadId]);

  useEffect(() => {
    sessionInitRef.current = (async () => {
      // The legacy mediation SID is optional. Saved account conversations must
      // still initialise when that endpoint is slow, retired, or unavailable.
      // Start it in parallel instead of putting it on the critical path to the
      // user's saved thread. The account thread becomes the authoritative live
      // session as soon as it resolves.
      startSession().then((sessionId) => {
        if (sessionId && !sessionIdRef.current) sessionIdRef.current = sessionId;
      }).catch(() => {});
      try {
        markLoginLatency('conversation_history_load_started');
        const archivedRequest = listArchivedConversationThreads()
          .then((archived) => setArchivedThreads(archived.threads || []))
          .catch(() => setArchivedThreads([]));
        const data = await listConversationThreads();
        markLoginLatency('conversation_list_complete');
        setIsHistorySavingEnabled(
          typeof data.save_conversation_history === 'boolean'
            ? data.save_conversation_history
            : null,
        );
        let thread = selectConversationToResume(data.threads);
        if (!thread) thread = await createConversationThread();
        sessionIdRef.current = thread.id;
        activeThreadIdRef.current = thread.id;
        setActiveThreadId(thread.id);
        const listedThreads = data.threads || [];
        setThreads(listedThreads.some((item) => item.id === thread.id)
          ? listedThreads
          : [thread, ...listedThreads]);
        const loaded = await loadConversationMessages(thread.id);
        markLoginLatency('active_thread_messages_complete');
        forceConversationBottomRef.current = true;
        setMessages(loaded.messages || []);
        markLoginLatency('conversation_history_load_complete');
        markLoginLatency('conversation_ready_render');
        void archivedRequest;
        return thread.id;
      } catch (err) {
        console.error('[ChatShell] Failed to load saved conversations');
        // A transient reload failure must not erase a transcript that React
        // has already rendered. Keep the last owned view in place and let the
        // person retry instead of replacing it with an empty conversation.
        setError('Saved conversations are temporarily unavailable. Please refresh in a moment.');
      } finally {
        markLoginLatency('conversation_history_load_complete');
        reportLoginLatencyTrace();
        setIsThreadLoading(false);
        markLoginLatency('composer_usable');
        reportLoginLatencyTrace();
      }
    })();
  }, []);

  useEffect(() => {
    markLoginLatency('chat_first_meaningful_render');
    reportLoginLatencyTrace();
  }, []);

  useEffect(() => {
    return () => {
      for (const pending of pendingDisplayEvidenceRef.current.values()) {
        pending.tracker.emit('delivery_uncertain');
      }
      pendingDisplayEvidenceRef.current.clear();
      try {
        recognitionRef.current?.stop?.();
      } catch (_) {}
      if (bargeInTimerRef.current) clearTimeout(bargeInTimerRef.current);
      if (bargeInDecisionTimerRef.current) clearTimeout(bargeInDecisionTimerRef.current);
      if (bargeInActivityTimerRef.current) clearInterval(bargeInActivityTimerRef.current);
      liveProgressWatchdogRef.current?.clear?.();
      stopSpeaking();
      stopAudioMeter();
      try {
        mediaStreamRef.current?.getTracks?.().forEach((t) => t.stop());
      } catch (_) {}
      try {
        audioContextRef.current?.close?.();
      } catch (_) {}
      voiceObservationQueueRef.current.clear();
      voiceSessionControllerRef.current?.teardown?.();
    };
  }, []);

  const stopSpeaking = () => {
    if (activeDeliveryTrackerRef.current && activePlaybackEvidenceRef.current) {
      activeDeliveryTrackerRef.current.emit('cancelled', {
        playbackRef: activePlaybackEvidenceRef.current,
      });
      activeDeliveryTrackerRef.current = null;
      activePlaybackEvidenceRef.current = '';
    }
    cancelBrightResolveVoice();

    try {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    } catch (_) {}
    currentUtteranceRef.current = null;
    setPlayingMessageId(null);

    if (isLiveOpenRef.current && !isMutedRef.current) {
      setLiveVoiceState('listening');
      liveVoiceStateRef.current = 'listening';
    }
  };

  const interruptLiveVoice = () => {
    if (
      !isLiveOpenRef.current ||
      isMutedRef.current ||
      liveVoiceStateRef.current !== 'speaking'
    ) return;

    traceVoiceLifecycleIncident('manual_interruption', {
      interruption_mode: 'manual',
      playback_state: 'active',
      owner_valid: true,
    });
    activeDeliveryTrackerRef.current?.emit('interrupted', {
      playbackRef: activePlaybackEvidenceRef.current,
    });
    activeDeliveryTrackerRef.current = null;
    activePlaybackEvidenceRef.current = '';
    bargeInInterruptedRef.current = true;
    bargeInActiveRef.current = false;
    bargeInPausedRef.current = false;
    abortVoiceTurn(liveRequestAbortRef);
    advanceVoiceGeneration(liveVoiceGenerationRef);
    voiceLatencyRef.current.cancel();
    if (bargeInTimerRef.current) clearTimeout(bargeInTimerRef.current);
    if (bargeInDecisionTimerRef.current) clearTimeout(bargeInDecisionTimerRef.current);
    if (bargeInActivityTimerRef.current) clearInterval(bargeInActivityTimerRef.current);
    bargeInActivityTimerRef.current = null;
    bargeInDecisionTimerRef.current = null;
    stopRecognition();
    cancelBrightResolveVoice();
    try { window.speechSynthesis?.cancel?.(); } catch (_) {}
    setIsRecording(false);
    setAssistantPreview('');
    setLiveTranscript('');
    setLiveVoiceState('listening');
    liveVoiceStateRef.current = 'listening';
    window.setTimeout(() => {
      if (isLiveOpenRef.current && !isMutedRef.current) startLiveRecognition();
    }, 80);
  };

  const speakText = (text, messageId = null, { forLive = false } = {}) => {
    if (!text) return;

    if (
      typeof window === 'undefined' ||
      !window.speechSynthesis ||
      typeof window.SpeechSynthesisUtterance === 'undefined'
    ) {
      setError('Speech playback is not available in this browser.');
      return;
    }

    try {
      window.speechSynthesis.cancel();
    } catch (_) {}

    const utterance = new window.SpeechSynthesisUtterance(text);

const voices = window.speechSynthesis.getVoices();





const preferredVoice =
    voices.find(v => /Microsoft Mark/i.test(v.name)) ||
    voices.find(v => /Google US English/i.test(v.name)) ||
    voices.find(v => /Google UK English/i.test(v.name)) ||
    voices.find(v => /Alex/i.test(v.name)) ||
    voices.find(v => /English/i.test(v.lang)) ||
    voices[0];

if (preferredVoice) {
    utterance.voice = preferredVoice;
}

utterance.pitch = 0.55;
utterance.rate = 0.88;
utterance.lang = "en-US";




utterance.pitch = 0.68;
utterance.rate = 0.92;
utterance.volume = 1.0;

// Robotic / neutral settings
utterance.lang = "en-US";


    currentUtteranceRef.current = utterance;

    utterance.onstart = () => {
      setPlayingMessageId(messageId);
      if (forLive) {
        setLiveVoiceState('speaking');
        liveVoiceStateRef.current = 'speaking';
        startVoiceBargeInMonitoring(text);
      }
    };

    utterance.onend = () => {
      setPlayingMessageId(null);
      currentUtteranceRef.current = null;

      if (forLive) {
        if (bargeInInterruptedRef.current) return;
        recentAssistantSpeechRef.current = {
          ...recentAssistantSpeechRef.current,
          endedAt: Date.now(),
        };
        setAssistantPreview('');
        setLiveTranscript('');
        if (isLiveOpenRef.current && !isMutedRef.current) {
          setLiveVoiceState('listening');
          liveVoiceStateRef.current = 'listening';
          restartLiveRecognition();
        } else {
          setLiveVoiceState(isMutedRef.current ? 'muted' : 'idle');
          liveVoiceStateRef.current = isMutedRef.current ? 'muted' : 'idle';
        }
      }
    };

    utterance.onerror = (event) => {
      console.error('[ChatShell] Speech playback failed');
      setPlayingMessageId(null);
      currentUtteranceRef.current = null;
      if (forLive) {
        setLiveVoiceState('error');
        liveVoiceStateRef.current = 'error';
      }
      setError(`Speech playback failed${event?.error ? `: ${event.error}` : '.'}`);
    };

    // Safari/WebKit can silently discard an utterance when cancel() and
    // speak() occur in the same event-loop tick after an async request.
    window.setTimeout(() => {
      if (currentUtteranceRef.current !== utterance) return;

      try {
        window.speechSynthesis.resume();
        window.speechSynthesis.speak(utterance);
      } catch (_) {
        console.error('[ChatShell] Could not start speech playback');
        currentUtteranceRef.current = null;
        setPlayingMessageId(null);

        if (forLive) {
          setLiveVoiceState('error');
          liveVoiceStateRef.current = 'error';
        }

        setError('Speech playback could not start.');
      }
    });
  };

  const appendAssistantMessage = (
    mainText,
    explanation = null,
    conversationId = null,
    responseId = null,
  ) => {
    const safeText = String(mainText || '').trim();
    if (!safeText || (conversationId && activeThreadIdRef.current !== conversationId)) return;

    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.sender === 'bot' && last.text === safeText) return prev;
      return [...prev, { id: responseId || genClientMessageId(), sender: 'bot', text: safeText, explanation, created_at: new Date().toISOString() }];
    });
  };

  const appendUserMessage = (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) return;
    setMessages((prev) => [...prev, { id: genClientMessageId(), sender: 'user', text: trimmed, created_at: new Date().toISOString() }]);
  };

  const openThread = async (thread) => {
    if (!thread?.id || thread.id === activeThreadId) return;
    stopSpeaking(); setIsThreadLoading(true);
    try {
      const loaded = await loadConversationMessages(thread.id);
      sessionIdRef.current = thread.id;
      activeThreadIdRef.current = thread.id;
      setActiveThreadId(thread.id);
      forceConversationBottomRef.current = true;
      setMessages(loaded.messages || []);
    } catch (_) { setError('Could not open that conversation.'); }
    finally { setIsThreadLoading(false); }
  };

  const newThread = async () => {
    stopSpeaking(); setIsThreadLoading(true);
    try {
      const thread = await createConversationThread();
      sessionIdRef.current = thread.id;
      activeThreadIdRef.current = thread.id;
      setActiveThreadId(thread.id);
      shouldFollowMessagesRef.current = true;
      setMessages([]);
      setThreads((previous) => [thread, ...previous]);
    } catch (_) { setError('Could not create a new conversation.'); }
    finally { setIsThreadLoading(false); }
  };

  const deleteThread = async (thread) => {
    if (!thread?.id || isThreadLoading) return;
    if (!window.confirm(`Archive “${thread.title || 'this conversation'}”? You can restore it later.`)) return;
    stopSpeaking(); setIsThreadLoading(true); setError('');
    try {
      await deleteConversationThread(thread.id);
      setArchivedThreads((previous) => [{ ...thread, archived_at: new Date().toISOString() }, ...previous]);
      const remaining = threads.filter((item) => item.id !== thread.id);
      setThreads(remaining);
      if (thread.id === activeThreadId) {
        if (remaining[0]) {
          await openThread(remaining[0]);
        } else {
          const replacement = await createConversationThread();
          sessionIdRef.current = replacement.id;
          activeThreadIdRef.current = replacement.id;
          setActiveThreadId(replacement.id);
          setMessages([]); setThreads([replacement]);
        }
      }
    } catch (_) { setError('Could not archive that conversation.'); }
    finally { setIsThreadLoading(false); }
  };

  const restoreThread = async (thread) => {
    if (!thread?.id || isThreadLoading) return;
    setIsThreadLoading(true); setError('');
    try {
      await restoreConversationThread(thread.id);
      setArchivedThreads((previous) => previous.filter((item) => item.id !== thread.id));
      setThreads((previous) => [{ ...thread, archived_at: undefined }, ...previous]);
    } catch (_) { setError('Could not restore that conversation.'); }
    finally { setIsThreadLoading(false); }
  };

  const permanentlyDeleteThread = async (thread) => {
    if (!thread?.id || isThreadLoading) return;
    if (!window.confirm(`Permanently delete “${thread.title || 'this conversation'}”? This cannot be undone.`)) return;
    setIsThreadLoading(true); setError('');
    try {
      await permanentlyDeleteConversationThread(thread.id);
      setArchivedThreads((previous) => previous.filter((item) => item.id !== thread.id));
    } catch (_) { setError('Could not permanently delete that conversation.'); }
    finally { setIsThreadLoading(false); }
  };

  const startEditingMessage = (message) => {
    if (!message?.id || message.sender !== 'user' || isSending) return;
    stopSpeaking(); setEditingMessageId(message.id); setInput(message.text || ''); setError('');
  };

  const cancelEditingMessage = () => { setEditingMessageId(null); setInput(''); };

  const submitPrompt = async (rawPrompt, opts = {}) => {
    if (isThreadLoading) return '';
    if (isSending && !(opts.live && opts.replaceActiveLiveTurn)) return '';

    setError('');
    const trimmed = String(rawPrompt || '').trim();
    if (!trimmed) return '';

    // Every async turn is bound to the conversation that accepted it. A tab,
    // sidebar, or new-chat change while the request is in flight must never
    // let the stale callback append into whichever thread is visible later.
    let requestThreadId = activeThreadIdRef.current || null;
    let requestSessionId = sessionIdRef.current || null;

    const editedMessageId = editingMessageId;
    setIsSending(true);
    if (editedMessageId) {
      setMessages((previous) => {
        const index = previous.findIndex((item) => item.id === editedMessageId && item.sender === 'user');
        if (index < 0) return previous;
        return previous.slice(0, index + 1).map((item, itemIndex) => itemIndex === index ? { ...item, text: trimmed } : item);
      });
      setEditingMessageId(null);
    } else {
      appendUserMessage(trimmed);
    }

    if (!opts.keepComposerText) {
      setInput('');
    }
    const liveTurn = opts.live
      ? beginAbortableVoiceTurn(liveRequestAbortRef, liveVoiceGenerationRef)
      : null;
    const liveVoiceGeneration = liveTurn?.generation ?? null;
    if (opts.live) livePendingGenerationRef.current = liveVoiceGeneration;
    let liveRequestTimedOut = false;
    // The transport deadline belongs to the network request, not account or
    // conversation restoration. Starting it here previously let a cold login
    // consume most (or all) of the 5.5-second budget before /process was even
    // dispatched, so the first voice turn timed out while a later text turn
    // worked normally.
    let liveRequestTimeoutId = null;

    if (opts.live) {
      const requestedUserTurnId = Number(opts.voiceUserTurnId || 0);
      if (requestedUserTurnId > 0) {
        activeVoiceUserTurnIdRef.current = requestedUserTurnId;
      } else {
        activeVoiceUserTurnIdRef.current = voiceUserTurnSequenceRef.current + 1;
        voiceUserTurnSequenceRef.current = activeVoiceUserTurnIdRef.current;
      }
      voiceLatencyRef.current.beginIfNeeded();
      voiceLatencyRef.current.mark('final_transcript');
      // Acceptance is immediate, but durable commit is server-owned and may
      // intentionally be disabled by the account's history preference.
      traceVoiceLifecycle('user_turn_accepted');
      if (opts.voiceInterruption === true) {
        voiceLatencyRef.current.markIfMissingAfter('speech_ended', 'speech_started');
        voiceLatencyRef.current.mark('replacement_turn_accepted');
        traceVoiceLifecycle('replacement_turn_accepted');
      }
      pendingLiveUserTranscriptRef.current = trimmed;
      setLiveVoiceState('thinking', {
        userTranscript: '',
        assistantText: '',
      });
      // React may receive the final LISTENING caption and the THINKING
      // transition in one browser task. Publish the cleared caption
      // explicitly so the completed user text cannot survive that handoff.
      setLiveTranscript('');
      liveVoiceStateRef.current = 'thinking';
      traceVoiceLifecycle('thinking_started');
      if (
        !isLiveOpenRef.current ||
        isMutedRef.current ||
        !isCurrentVoiceGeneration(liveVoiceGenerationRef, liveVoiceGeneration)
      ) {
        if (livePendingGenerationRef.current === liveVoiceGeneration) {
          livePendingGenerationRef.current = null;
        }
        pendingLiveUserTranscriptRef.current = '';
        setIsSending(false);
        return '';
      }
      // Enter thinking immediately after the accepted transcript paints. A
      // cold conversation restore must not leave the orb apparently listening
      // or let the pre-dispatch watchdog discard the person's first turn.
      liveProgressWatchdogRef.current?.clear?.();
      // THINKING has no active speaker handoff, so every supported runtime can
      // monitor provisionally for a deliberate replacement. Playback remains
      // exclusive on Safari only once Sonny actually starts speaking.
      startLiveRecognition({
        whileThinking: true,
        generation: liveVoiceGeneration,
      });
    } else {
      setLiveTranscript('');
    }

    try {
      await (sessionInitRef.current || Promise.resolve());
    } catch (_) {}

    if (
      opts.live &&
      (!isLiveOpenRef.current ||
        isMutedRef.current ||
        !isCurrentVoiceGeneration(liveVoiceGenerationRef, liveVoiceGeneration))
    ) {
      liveProgressWatchdogRef.current?.clear?.();
      if (livePendingGenerationRef.current === liveVoiceGeneration) {
        livePendingGenerationRef.current = null;
      }
      pendingLiveUserTranscriptRef.current = '';
      setIsSending(false);
      return '';
    }

    requestThreadId ||= activeThreadIdRef.current || null;
    requestSessionId ||= sessionIdRef.current || null;

    // Thread history improves continuity, but it must not be a single point of
    // failure for a verified account's live conversation. When the history
    // service is temporarily unavailable, retain the server session and omit
    // thread_id rather than fabricating one or blocking the conversation.
    const threadId = requestThreadId;
    if (!requestSessionId) {
      setError('Your secure session is still preparing. Please try again in a moment.');
      if (livePendingGenerationRef.current === liveVoiceGeneration) {
        livePendingGenerationRef.current = null;
      }
      if (opts.live) pendingLiveUserTranscriptRef.current = '';
      setIsSending(false);
      return '';
    }

    const liveTurnOwner = opts.live ? captureVoiceTurnOwner({
      generationId: liveVoiceGeneration,
      voiceSessionId: voiceSessionIdRef.current,
      conversationId: requestThreadId,
      expectedStates: ['thinking', 'interruption_check', 'speaking', 'recovering'],
    }) : null;
    const isCurrentLiveVoiceRequest = (expectedStates) => {
      const current = opts.live === true && isLiveOpenRef.current && isCurrentVoiceTurnOwner(
        liveTurnOwner,
        {
          generationId: liveVoiceGenerationRef.current,
          voiceSessionId: voiceSessionIdRef.current,
          conversationId: activeThreadIdRef.current,
          state: liveVoiceStateRef.current,
          expectedStates,
        },
      );
      if (opts.live && !current && voiceLatencyRef.current.isActive()) {
        voiceLatencyRef.current.mark('stale_callback_ignored');
      }
      return current;
    };

    try {
      if (opts.live) {
        liveRequestTimeoutId = liveTurn?.controller
          ? window.setTimeout(() => {
              liveRequestTimedOut = true;
              liveTurn.controller.abort();
            }, LIVE_REQUEST_TIMEOUT_MS)
          : null;
        voiceLatencyRef.current.mark('api_dispatched');
        if (opts.voiceInterruption === true) {
          voiceLatencyRef.current.mark('replacement_api_dispatched');
          traceVoiceLifecycle('replacement_api_dispatched');
          traceVoiceIncident('replacement_dispatched', {
            playback_state: 'inactive',
            owner_valid: true,
          });
        }
        voiceLatencyRef.current.mark('model_requested');
        traceVoiceLifecycle('ai_request_started');
      }
      const result = await sendMessage(trimmed, {
        allowApiFallback: true,
        // Live Voice keeps a conversationally concise allowance so the full
        // response reaches TTS sooner without producing sentence fragments.
        maxTokens: opts.live === true ? LIVE_RESPONSE_MAX_TOKENS : 384,
        voiceMode: opts.live === true,
        voiceInterruption: opts.voiceInterruption === true,
        voiceSessionId: voiceSessionIdRef.current,
        voiceGenerationId: liveVoiceGeneration,
        voiceIncidentRef: opts.voiceIncidentRef,
        temperature: 0.2,
        topP: 1.0,
        sessionId: requestSessionId,
        threadId: threadId || undefined,
        editedMessageId: editedMessageId || undefined,
        signal: liveTurn?.signal,
        onResponseHeaders: opts.live === true
          ? () => voiceLatencyRef.current.mark('api_response_headers')
          : undefined,
        onResponseBody: opts.live === true
          ? () => voiceLatencyRef.current.mark('api_response_body')
          : undefined,
      });

      const mainText = (
        typeof result === 'string'
          ? result
          : String(
              result?.normalized ||
              result?.parsed?.response ||
              result?.parsed?.reply ||
              result?.parsed?.result ||
              result?.parsed?.text ||
              result?.parsed?.message ||
              result?.parsed?.ai_response ||
              result?.parsed?.raw_ai ||
              result?.parsed?.output ||
              result?.parsed?.content ||
              result?.raw ||
              ''
            )
      ).trim();
      const turnAction = result?.turnAction || result?.parsed?.turn_action || 'assistant_response';
      const deliveryTracker = createFrontendDeliveryTracker(
        result?.parsed?.delivery_evidence,
      );
      deliveryTracker?.emit('response_received');
      if (result?.parsed?.conversation_storage === 'history_disabled') {
        setIsHistorySavingEnabled(false);
      } else if (result?.parsed?.conversation_saved === true) {
        setIsHistorySavingEnabled(true);
      }
      if (opts.live) {
        voiceLatencyRef.current.mark('api_response');
        voiceLatencyRef.current.observeServerTiming(
          result?.serverTiming || result?.parsed?.voice_timing,
        );
        voiceLatencyRef.current.observeProcessMetadata({
          requestId: result?.requestId,
          buildSha: result?.buildSha,
        });
        voiceLatencyRef.current.mark('model_returned');
        traceVoiceLifecycle('ai_response_received');
        if (opts.voiceInterruption === true) {
          traceVoiceIncident('backend_result', {
            playback_state: 'inactive',
            owner_valid: true,
            replacement_request_id: result?.requestId || '',
            backend_accepted: true,
            backend_duplicate: result?.parsed?.duplicate === true,
          });
          voiceIncidentRef.current = '';
        }
        voiceLatencyRef.current.mark('response_validated');
      }

      if (turnAction === 'hold_floor') {
        if (!opts.live || !isCurrentLiveVoiceRequest(['thinking', 'interruption_check'])) {
          return '';
        }
        // HOLD_FLOOR owns no assistant text, speech, or playback. Release the
        // current request owner and return the same orb directly to listening.
        liveProgressWatchdogRef.current?.clear?.();
        stopImmediateBargeInActivityMonitor();
        stopRecognition();
        bargeInActiveRef.current = false;
        pendingLiveUserTranscriptRef.current = '';
        setAssistantPreview('');
        setLiveTranscript('');
        traceVoiceLifecycle('hold_floor_accepted');
        voiceLatencyRef.current.complete();
        setLiveVoiceState('listening', {
          userTranscript: '',
          assistantText: '',
        });
        liveVoiceStateRef.current = 'listening';
        restartLiveRecognition();
        return '';
      }

      if (!mainText) {
        deliveryTracker?.emit('unavailable');
        if (opts.live) {
          setLiveVoiceState('error');
          liveVoiceStateRef.current = 'error';
        }
        setError('The model returned no output. Please try again.');
        return '';
      }

      if (opts.live && !isCurrentLiveVoiceRequest(['thinking', 'interruption_check'])) {
        deliveryTracker?.invalidate();
        return '';
      }

      const deliveryResponseRef = deliveryTracker?.identity?.assistant_response_ref || null;
      if (deliveryTracker && activeThreadIdRef.current === requestThreadId) {
        pendingDisplayEvidenceRef.current.set(deliveryResponseRef, {
          tracker: deliveryTracker,
          conversationRef: requestThreadId,
        });
      }
      appendAssistantMessage(
        mainText,
        result?.parsed?.explanation || null,
        requestThreadId,
        deliveryResponseRef,
      );

      if (isCurrentLiveVoiceRequest(['thinking', 'interruption_check'])) {
        console.timeStamp('[VOICE] starting speech playback');
        const exclusivePlayback = requiresExclusiveVoicePlayback();
        const monitorMode = getDuplexVoiceMonitoringMode();
        // Safari requires an exclusive microphone-to-speaker handoff or a
        // successfully downloaded response can remain inaudible. Browsers
        // with stable duplex Web Speech keep the mic stream and monitor for a
        // deliberate, finalized interruption while Sonny continues speaking.
        if (exclusivePlayback) releaseLiveInputAudio();
        else stopRecognition();
        // Keep the accepted user transcript visible until audio genuinely
        // starts. Clearing it when the model returns creates a blank mobile
        // interval while TTS is still downloading or acquiring the speaker.
        setAssistantPreview(mainText);
        recentAssistantSpeechRef.current = { text: mainText, endedAt: 0 };
        bargeInInterruptedRef.current = false;
        bargeInPausedRef.current = false;
        bargeInPendingRef.current = null;
        bargeInCycleOpenRef.current = false;
        bargeInSpeechActiveRef.current = false;
        bargeInIgnoreResultsUntilRef.current = 0;
        const deliveryPlaybackRef = deliveryTracker ? createPlaybackRef() : '';
        let deliveryAudioReady = false;
        let deliveryPlaybackRequested = false;
        activeDeliveryTrackerRef.current = deliveryTracker;
        activePlaybackEvidenceRef.current = deliveryPlaybackRef;
        deliveryTracker?.emit('tts_requested', {
          playbackRef: deliveryPlaybackRef,
        });
        traceVoiceLifecycle('tts_requested');
        traceVoiceLifecycleIncident('tts_requested', { playback_state: 'requested' });
        speakWithBrightResolveVoice(mainText, {
          preferSpeakerRoute: exclusivePlayback,
          latencyRef: voiceLatencyRef.current.currentRef(),
          onAudioSessionTransition: ({ state, applied }) => {
            traceVoiceLifecycleIncident('audio_session_transition', {
              audio_session_state: state,
              transition_result: applied ? 'applied' : 'unavailable',
            });
          },
          onPlaybackAttempt: ({ attempt }) => {
            if (!deliveryPlaybackRequested) {
              deliveryPlaybackRequested = true;
              deliveryTracker?.emit('playback_requested', {
                playbackRef: deliveryPlaybackRef,
              });
            }
            voiceLatencyRef.current.mark('playback_requested');
            traceVoiceLifecycleIncident('html_audio_attempt', {
              playback_state: 'starting',
              transition_result: attempt > 1 ? 'retry' : 'initial',
            });
          },
          onPlaybackRetry: () => {
            traceVoiceLifecycleIncident('html_audio_retry', {
              playback_state: 'starting',
              transition_result: 'retry',
            });
          },
          onPlaybackCancelled: () => {
            deliveryTracker?.emit('cancelled', {
              playbackRef: deliveryPlaybackRef,
            });
            if (activeDeliveryTrackerRef.current === deliveryTracker) {
              activeDeliveryTrackerRef.current = null;
              activePlaybackEvidenceRef.current = '';
            }
            traceVoiceLifecycleIncident('playback_cancelled', {
              playback_state: 'cancelled',
              invalidation_reason: 'owner_cancelled',
            });
          },
          onRequest: () => {
            if (isCurrentLiveVoiceRequest(['thinking', 'interruption_check'])) {
              voiceLatencyRef.current.mark('tts_dispatched');
            }
          },
          onAudioReady: () => {
            if (isCurrentLiveVoiceRequest(['thinking', 'interruption_check'])) {
              if (!deliveryAudioReady) {
                deliveryAudioReady = true;
                deliveryTracker?.emit('audio_ready', {
                  playbackRef: deliveryPlaybackRef,
                });
              }
              const metric = traceVoiceLifecycle('tts_audio_body_received');
              traceVoiceLifecycleIncident('tts_audio_body_received', {
                playback_state: 'preparing',
                media_event_elapsed_ms: metric?.turn_elapsed_ms || 0,
              });
              traceVoiceLifecycle('first_audio_ready');
              if (!exclusivePlayback) {
                voiceLatencyRef.current.mark('route_handoff_started');
                voiceLatencyRef.current.mark('route_handoff_completed');
              }
            }
          },
          onResponse: ({ serverTiming } = {}) => {
            if (isCurrentLiveVoiceRequest(['thinking', 'interruption_check'])) {
              traceVoiceLifecycle('tts_response_received');
              voiceLatencyRef.current.observeTtsTiming(parseTtsServerTimingHeader(serverTiming));
            }
          },
          onAudioDecoded: () => {
            if (isCurrentLiveVoiceRequest(['thinking', 'interruption_check'])) {
              traceVoiceLifecycle('audio_decoded');
            }
          },
          onRouteHandoffStart: () => {
            if (isCurrentLiveVoiceRequest(['thinking', 'interruption_check'])) {
              const metric = traceVoiceLifecycle('route_handoff_started');
              traceVoiceLifecycleIncident('route_handoff_started', {
                playback_state: 'preparing',
                media_event_elapsed_ms: metric?.turn_elapsed_ms || 0,
              });
            }
          },
          onRouteHandoffComplete: () => {
            if (isCurrentLiveVoiceRequest(['thinking', 'interruption_check'])) {
              const metric = traceVoiceLifecycle('route_handoff_completed');
              traceVoiceLifecycleIncident('route_handoff_completed', {
                playback_state: 'preparing',
                media_event_elapsed_ms: metric?.turn_elapsed_ms || 0,
              });
            }
          },
          onMediaLifecycle: ({
            stage,
            readyState,
            networkState,
            sourcePhase,
            positionState,
            attempt,
          }) => {
            const ownerValid = isCurrentLiveVoiceRequest([
              'thinking',
              'interruption_check',
              'speaking',
            ]);
            const metric = voiceLatencyRef.current.isActive()
              ? traceVoiceLifecycle(stage, { owner_valid: ownerValid })
              : null;
            traceVoiceLifecycleIncident(stage, {
              playback_state: ownerValid ? 'preparing' : 'stale',
              owner_valid: ownerValid,
              media_ready_state: readyState,
              media_network_state: networkState,
              media_source_phase: sourcePhase,
              media_position_state: positionState,
              playback_attempt: attempt,
              media_event_elapsed_ms: metric?.turn_elapsed_ms || 0,
              stale_callback: !ownerValid,
              invalidation_reason: ownerValid ? 'unavailable' : 'media_lifecycle_owner',
            });
          },
          onStart: () => {
            if (!isCurrentLiveVoiceRequest(['thinking', 'interruption_check'])) {
              deliveryTracker?.invalidate();
              traceVoiceLifecycleIncident('stale_callback_rejected', {
                stale_callback: true,
                invalidation_reason: 'playback_start_owner',
              });
              return;
            }
            deliveryTracker?.emit('playback_started', {
              playbackRef: deliveryPlaybackRef,
            });
            traceVoiceLifecycleIncident('playback_started', { playback_state: 'active' });
            voiceLatencyRef.current.mark('playback_started');
            if (opts.voiceInterruption === true) {
              voiceLatencyRef.current.mark('replacement_speaking_started');
            }
            const browser = voiceBrowserMetadata();
            voiceLatencyRef.current.reportFirstAudible({
              browserFamily: browser.browser_family,
              appleDeviceClass: browser.apple_device_class,
            });
            traceVoiceLifecycle('speaking_started');
            pendingLiveUserTranscriptRef.current = '';
            setLiveVoiceState('speaking', {
              userTranscript: '',
              assistantText: mainText,
            });
            liveVoiceStateRef.current = 'speaking';
            if (!isMutedRef.current && monitorMode) {
              if (!exclusivePlayback && monitorMode === 'web_speech') {
                startImmediateBargeInActivityMonitor();
              }
              // Spoken barge-in is the primary interaction. The transparent
              // center button remains accessibility-only. If Apple's bounded
              // recorder cannot acquire the microphone, immediately fall back
              // to Web Speech when the runtime exposes it instead of leaving
              // the session silently unable to hear an interruption.
              startVoiceBargeInMonitoring(mainText);
            }
          },
        }).then(() => {
          if (!isCurrentLiveVoiceRequest(['thinking', 'interruption_check', 'speaking'])) {
            deliveryTracker?.invalidate();
            traceVoiceLifecycleIncident('stale_callback_rejected', {
              stale_callback: true,
              invalidation_reason: 'playback_complete_owner',
            });
            return;
          }
          deliveryTracker?.emit('playback_completed', {
            playbackRef: deliveryPlaybackRef,
          });
          if (activeDeliveryTrackerRef.current === deliveryTracker) {
            activeDeliveryTrackerRef.current = null;
            activePlaybackEvidenceRef.current = '';
          }
          traceVoiceLifecycleIncident('playback_completed', { playback_state: 'completed' });
          if (bargeInInterruptedRef.current) return;
          if (bargeInDecisionTimerRef.current) clearTimeout(bargeInDecisionTimerRef.current);
          bargeInDecisionTimerRef.current = null;
          bargeInPausedRef.current = false;
          bargeInPendingRef.current = null;
          bargeInCycleOpenRef.current = false;
          bargeInSpeechActiveRef.current = false;
          recentAssistantSpeechRef.current = { text: mainText, endedAt: Date.now() };
          bargeInActiveRef.current = false;
          stopImmediateBargeInActivityMonitor();
          stopRecognition();
          if (isLiveOpenRef.current && !isMutedRef.current) {
            voiceLatencyRef.current.mark('speaking_finished');
            voiceLatencyRef.current.complete();
            setLiveVoiceState('listening', {
              userTranscript: '',
              assistantText: '',
            });
            liveVoiceStateRef.current = 'listening';
            restartLiveRecognition();
          } else if (isLiveOpenRef.current) {
            setLiveVoiceState('muted', {
              userTranscript: '',
              assistantText: '',
            });
            liveVoiceStateRef.current = 'muted';
          }
        }).catch((ttsError) => {
          if (!isCurrentLiveVoiceRequest(['thinking', 'interruption_check', 'speaking', 'recovering'])) {
            deliveryTracker?.invalidate();
            traceVoiceLifecycleIncident('stale_callback_rejected', {
              stale_callback: true,
              invalidation_reason: 'playback_failure_owner',
            });
            return;
          }
          deliveryTracker?.emit('failed', {
            playbackRef: deliveryPlaybackRef,
          });
          if (activeDeliveryTrackerRef.current === deliveryTracker) {
            activeDeliveryTrackerRef.current = null;
            activePlaybackEvidenceRef.current = '';
          }
          traceVoiceLifecycleIncident('playback_failed', {
            playback_state: 'failed',
            invalidation_reason: String(ttsError?.ttsStage || 'unknown').slice(0, 48),
          });
          console.error('[BRIGHTRESOLVE TTS] Playback unavailable');
          traceVoiceLifecycle('tts_failed', {
            failure_stage: ttsError?.ttsStage || 'unknown',
          });
          // Never impersonate Sonny with an operating-system/browser voice.
          // Keep the generated response visible and return to listening so a
          // temporary provider outage cannot silently change voice identity.
          stopImmediateBargeInActivityMonitor();
          stopRecognition();
          bargeInActiveRef.current = false;
          // Preserve the generated answer in the voice surface. Safari can
          // reject an otherwise successful audio handoff after the response
          // and TTS bytes have both arrived; that must never erase the reply.
          pendingLiveUserTranscriptRef.current = '';
          setError('Sonny’s voice is temporarily unavailable. Please try again.');
          if (isLiveOpenRef.current && !isMutedRef.current) {
            setLiveVoiceState('listening', {
              userTranscript: '',
              assistantText: '',
            });
            liveVoiceStateRef.current = 'listening';
            traceVoiceLifecycle('tts_recovery_listening');
            voiceLatencyRef.current.cancel();
            restartLiveRecognition();
          } else if (isLiveOpenRef.current) {
            setLiveVoiceState('muted', {
              userTranscript: '',
              assistantText: '',
            });
            liveVoiceStateRef.current = 'muted';
            traceVoiceLifecycle('tts_recovery_muted');
            voiceLatencyRef.current.cancel();
          }
        });
      }

      return mainText;
    } catch (err) {
      if (
        opts.live &&
        err?.name === 'AbortError' &&
        liveRequestTimedOut &&
        isCurrentVoiceGeneration(liveVoiceGenerationRef, liveVoiceGeneration)
      ) {
        pendingLiveUserTranscriptRef.current = '';
        setError('That response took too long. Please try again.');
        if (isLiveOpenRef.current && !isMutedRef.current) {
          voiceLatencyRef.current.cancel();
          setLiveVoiceState('listening');
          liveVoiceStateRef.current = 'listening';
          restartLiveRecognition();
        }
        return '';
      }
      if (opts.live && (err?.name === 'AbortError' || !isCurrentVoiceGeneration(liveVoiceGenerationRef, liveVoiceGeneration))) {
        return '';
      }
      if (opts.live && opts.voiceInterruption === true) {
        const status = Number(err?.status);
        traceVoiceIncident('backend_result', {
          playback_state: 'inactive',
          classifier_action: 'request_failed',
          classifier_reason: Number.isInteger(status) ? `http_${status}` : 'transport_failure',
          owner_valid: true,
          replacement_request_id: err?.requestId || '',
          backend_accepted: false,
          backend_duplicate: false,
        });
        voiceIncidentRef.current = '';
      }
      console.error('[ChatShell] Request failed');
      if (editedMessageId && activeThreadIdRef.current) {
        loadConversationMessages(activeThreadIdRef.current).then((loaded) => setMessages(loaded.messages || [])).catch(() => {});
      }
      if (opts.live) {
        pendingLiveUserTranscriptRef.current = '';
        setLiveVoiceState('error');
        liveVoiceStateRef.current = 'error';
      }
      setError('Something went wrong. Please try again.');
      return '';
    } finally {
      if (liveRequestTimeoutId) window.clearTimeout(liveRequestTimeoutId);
      if (opts.live && isCurrentVoiceGeneration(liveVoiceGenerationRef, liveVoiceGeneration)) {
        liveRequestAbortRef.current = null;
      }
      if (!opts.live) setIsSending(false);
      else if (livePendingGenerationRef.current === liveVoiceGeneration) {
        livePendingGenerationRef.current = null;
        setIsSending(false);
      }
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (isThreadLoading) return;
    await submitPrompt(input);
  };

  const handlePlayMessage = (msg) => {
    if (!msg?.text) return;
    if (playingMessageId === msg.id) {
      stopSpeaking();
      return;
    }

    setError('');
    speakWithBrightResolveVoice(msg.text, {
      onStart: () => setPlayingMessageId(msg.id),
    }).catch(() => {
      console.error('[BRIGHTRESOLVE TTS] Playback unavailable');
      setError('BrightResolve voice is temporarily unavailable. Please try again.');
    }).finally(() => {
      setPlayingMessageId((current) => (current === msg.id ? null : current));
    });
  };

  const handleStopMessage = () => {
    stopSpeaking();
  };

  async function startAudioMeter() {
    if (micPermissionDeniedRef.current) return false;
    const reacquiring = voiceMicrophoneEverAcquiredRef.current;
    traceVoiceLifecycleIncident(
      reacquiring ? 'microphone_reacquire_attempt' : 'microphone_acquire_attempt',
      { microphone_state: reacquiring ? 'reacquiring' : 'acquiring' },
    );
    try {
      if (!navigator.mediaDevices?.getUserMedia) return false;

      if (!mediaStreamRef.current) {
        mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        const settings = mediaStreamRef.current?.getAudioTracks?.()?.[0]?.getSettings?.()
          || mediaStreamRef.current?.getTracks?.()?.[0]?.getSettings?.()
          || {};
        appliedAudioConstraintsRef.current = {
          echo_cancellation: appliedAudioConstraint(settings.echoCancellation),
          noise_suppression: appliedAudioConstraint(settings.noiseSuppression),
          auto_gain_control: appliedAudioConstraint(settings.autoGainControl),
        };
      }

      if (!liveInputTrackCleanupRef.current) {
        const ownedStream = mediaStreamRef.current;
        const tracks = ownedStream?.getTracks?.() || [];
        const handleTrackEnded = () => {
          if (mediaStreamRef.current !== ownedStream || !isLiveOpenRef.current) return;
          micPermissionDeniedRef.current = true;
          releaseLiveInputAudio();
          setLiveTranscript('');
          setLiveVoiceState('error');
          liveVoiceStateRef.current = 'error';
          setError('Microphone access ended. Allow microphone access, then reopen Live voice.');
        };
        tracks.forEach((track) => track.addEventListener?.('ended', handleTrackEnded, { once: true }));
        liveInputTrackCleanupRef.current = () => {
          tracks.forEach((track) => track.removeEventListener?.('ended', handleTrackEnded));
          liveInputTrackCleanupRef.current = null;
        };
      }

      if (!audioContextRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioCtx();
      }

      if (audioContextRef.current?.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      if (!sourceRef.current) {
        sourceRef.current = audioContextRef.current.createMediaStreamSource(mediaStreamRef.current);
      }

      if (!analyserRef.current) {
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.2;
        sourceRef.current.connect(analyserRef.current);
      }

      const analyser = analyserRef.current;
      const frameScheduler = createAnalyserFrameScheduler();
      const data = new Uint8Array(analyser.fftSize);
      let smoothed = 0;

      const tick = () => {
        if (!analyser || isMutedRef.current) {
          publishVoiceInputEnergy(0);
          rafRef.current = frameScheduler.request(tick);
          return;
        }

        analyser.getByteTimeDomainData(data);
        smoothed = measureVoiceEnergy(data, smoothed).level;
        latestMicrophoneEnergyRef.current = smoothed;
        publishVoiceInputEnergy(smoothed);
        rafRef.current = frameScheduler.request(tick);
      };

      if (rafRef.current) analyserFrameSchedulerRef.current.cancel(rafRef.current);
      analyserFrameSchedulerRef.current = frameScheduler;
      rafRef.current = frameScheduler.request(tick);
      if (voiceSessionOpeningRef.current && !voiceSessionOpeningRef.current.microphoneReady) {
        voiceSessionOpeningRef.current.microphoneReady = true;
        traceVoiceLifecycle('microphone_ready');
      }
      voiceMicrophoneEverAcquiredRef.current = true;
      traceVoiceLifecycleIncident(
        reacquiring ? 'microphone_reacquired' : 'microphone_acquired',
        { microphone_state: 'active' },
      );
      return true;
    } catch (err) {
      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
        micPermissionDeniedRef.current = true;
        setError('Microphone access is blocked. Allow microphone access, then open Live voice again.');
        if (isLiveOpenRef.current) {
          setLiveVoiceState('error');
          liveVoiceStateRef.current = 'error';
        }
        console.warn('[ChatShell] Microphone permission is unavailable.');
      } else {
        console.warn('[ChatShell] Audio meter is unavailable.');
      }
      traceVoiceLifecycleIncident(
        reacquiring ? 'microphone_reacquire_failed' : 'recognition_error',
        { microphone_state: 'unavailable', invalidation_reason: 'capture_unavailable' },
      );
      return false;
    }
  }

  function stopAudioMeter() {
    if (rafRef.current) analyserFrameSchedulerRef.current.cancel(rafRef.current);
    rafRef.current = null;
    analyserFrameSchedulerRef.current = null;
    latestMicrophoneEnergyRef.current = 0;
    publishVoiceInputEnergy(0);
  }

  const stopRecognition = () => {
    if (liveSilenceTimerRef.current) window.clearTimeout(liveSilenceTimerRef.current);
    liveSilenceTimerRef.current = null;
    if (recorderTimerRef.current) window.clearInterval(recorderTimerRef.current);
    recorderTimerRef.current = null;
    if (recorderDeadlineRef.current) window.clearTimeout(recorderDeadlineRef.current);
    recorderDeadlineRef.current = null;
    if (liveFinalTranscriptRecoveryRef.current) {
      window.clearTimeout(liveFinalTranscriptRecoveryRef.current);
      liveFinalTranscriptRecoveryRef.current = null;
    }
    const activeRecognition = recognitionRef.current;
    if (activeRecognition && activeRecognition === recordedBargeInRecorderRef.current) {
      recordedBargeInDiscardRef.current = true;
    }
    try {
      activeRecognition?.stop?.();
    } catch (_) {}
    recognitionRef.current = null;
  };

  const releaseLiveInputAudio = () => {
    const hadLiveInput = Boolean(mediaStreamRef.current || audioContextRef.current);
    liveInputTrackCleanupRef.current?.();
    stopRecognition();
    stopAudioMeter();
    try {
      sourceRef.current?.disconnect?.();
      analyserRef.current?.disconnect?.();
      mediaStreamRef.current?.getTracks?.().forEach((track) => track.stop());
      audioContextRef.current?.close?.();
    } catch (_) {}
    sourceRef.current = null;
    analyserRef.current = null;
    mediaStreamRef.current = null;
    audioContextRef.current = null;
    setIsRecording(false);
    if (hadLiveInput) {
      traceVoiceLifecycleIncident('microphone_released', { microphone_state: 'released' });
    }
  };

  const buildRecognition = () => {
    const SpeechRecognitionCtor = getSpeechRecognitionCtor();
    if (!SpeechRecognitionCtor) {
      setError('Voice input is not available in this browser.');
      return null;
    }

    stopRecognition();

    const recognition = new SpeechRecognitionCtor();
    recognitionGenerationRef.current += 1;
    recognition.__brightResolveGenerationId = recognitionGenerationRef.current;
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;
    return recognition;
  };

  const openTranscribe = async () => {
    setError('');
    setIsTranscribeOpen(true);
    transcribeKeepAliveRef.current = true;
    transcribeFinalBufferRef.current = input || '';
    await startAudioMeter();
    startTranscribeRecognition();
  };

  const closeTranscribe = () => {
    transcribeKeepAliveRef.current = false;
    stopRecognition();
    setIsRecording(false);
    setIsTranscribeOpen(false);
    stopAudioMeter();
  };

  const startTranscribeRecognition = () => {
    const recognition = buildRecognition();
    if (!recognition) return;

    recognition.onstart = () => {
      setIsRecording(true);
    };

    recognition.onresult = (event) => {
      let interim = '';

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const txt = event.results[i][0]?.transcript || '';
        if (event.results[i].isFinal) {
          transcribeFinalBufferRef.current = `${transcribeFinalBufferRef.current} ${txt}`.trim();
        } else {
          interim += txt;
        }
      }

      setInput(`${transcribeFinalBufferRef.current} ${interim}`.trim());
    };

    recognition.onerror = (event) => {
      setIsRecording(false);

      const err = event?.error || '';
      if (err === 'aborted' || err === 'no-speech') {
        return;
      }

      transcribeKeepAliveRef.current = false;
      setError('Voice transcription failed.');
    };

    recognition.onend = () => {
      setIsRecording(false);

      if (transcribeKeepAliveRef.current) {
        setTimeout(() => {
          if (transcribeKeepAliveRef.current && isTranscribeOpen) {
            startTranscribeRecognition();
          }
        }, 120);
      }
    };

    try {
      recognition.start();
    } catch (_) {
      console.error('[ChatShell] Transcribe start error');
      setError('Voice transcription could not start.');
    }
  };

  const finishTranscribe = () => {
    transcribeKeepAliveRef.current = false;
    stopRecognition();
    setIsRecording(false);
    setIsTranscribeOpen(false);
    stopAudioMeter();
    setInput((prev) => prev.trim());
  };

  const restartLiveRecognition = () => {
    if (!isLiveOpenRef.current || isMutedRef.current) return;
    if (liveVoiceStateRef.current === 'speaking' || liveVoiceStateRef.current === 'thinking') return;
    startLiveRecognition();
  };

  const isCurrentBargeInOwner = (candidateId, expectedStates) => {
    const owner = bargeInOwnerRef.current;
    if (!owner) return false;
    return isCurrentVoiceTurnOwner(owner, {
      generationId: liveVoiceGenerationRef.current,
      voiceSessionId: voiceSessionIdRef.current,
      conversationId: activeThreadIdRef.current,
      interruptionCandidateId: candidateId,
      sourceState: 'speaking',
      state: liveVoiceStateRef.current,
      expectedStates,
    });
  };

  const resumeAfterFalseBargeIn = (expectedCycle = null) => {
    if (
      expectedCycle !== null &&
      expectedCycle !== bargeInCycleGenerationRef.current
    ) return false;
    if (
      expectedCycle !== null &&
      !isCurrentBargeInOwner(expectedCycle, ['interruption_check', 'listening'])
    ) return false;
    // Recovery is never allowed to talk over a currently active speaker.
    // Keep this invariant here—not only in timer callers—because recognition,
    // recorded-audio, and error callbacks can all request false-alarm resume.
    if (bargeInSpeechActiveRef.current) return false;
    if (bargeInDecisionTimerRef.current) clearTimeout(bargeInDecisionTimerRef.current);
    bargeInDecisionTimerRef.current = null;
    if (!bargeInPausedRef.current) return false;
    traceVoiceLifecycleIncident('false_interruption_recovery', {
      interruption_mode: 'false_alarm',
      playback_state: 'paused',
      playback_offset_bucket: 'unavailable',
    });
    bargeInPausedRef.current = false;
    bargeInOwnerRef.current = null;
    bargeInPendingRef.current = null;
    bargeInCycleOpenRef.current = false;
    bargeInSpeechActiveRef.current = false;
    bargeInInterruptedRef.current = false;
    bargeInActiveRef.current = true;
    bargeInIgnoreResultsUntilRef.current = Date.now() + BARGE_IN_ACOUSTIC_COOLDOWN_MS;
    bargeInAcousticCooldownUntilRef.current = Date.now() + BARGE_IN_ACOUSTIC_COOLDOWN_MS;
    if (
      !isLiveOpenRef.current || isMutedRef.current ||
      !['speaking', 'listening', 'interruption_check'].includes(liveVoiceStateRef.current)
    ) {
      return false;
    }
    setLiveVoiceState('speaking');
    liveVoiceStateRef.current = 'speaking';
    setLiveTranscript('');
    setAssistantPreview(recentAssistantSpeechRef.current.text || '');
    voiceLatencyRef.current.mark('interruption_rejected');
    traceVoiceIncident('candidate_rejected', {
      playback_state: 'paused',
      classifier_action: 'reject',
      classifier_reason: 'false_alarm',
      owner_valid: true,
    });
    const resumedServerVoice = resumeBrightResolveVoice();
    if (!resumedServerVoice) {
      try {
        window.speechSynthesis?.resume?.();
        voiceLatencyRef.current.mark('playback_resumed');
        traceVoiceIncident('playback_resumed', {
          playback_state: 'active',
          owner_valid: true,
        });
        voiceIncidentRef.current = '';
        startImmediateBargeInActivityMonitor();
        return true;
      } catch (_) {
        return false;
      }
    }
    voiceLatencyRef.current.mark('playback_resumed');
    traceVoiceIncident('playback_resumed', {
      playback_state: 'active',
      owner_valid: true,
    });
    voiceIncidentRef.current = '';
    startImmediateBargeInActivityMonitor();
    return true;
  };

  const pauseForPotentialBargeIn = (
    recoveryMs = null,
  ) => {
    if (
      liveVoiceStateRef.current !== 'speaking' &&
      !(bargeInPausedRef.current && ['listening', 'interruption_check'].includes(liveVoiceStateRef.current))
    ) return false;

    // Pause immediately on every new speech event, including a later event
    // after a false alarm resumed this same reply. Re-arm recovery for every
    // credible interim update so Sonny never restarts over continuing speech.
    let cycle = bargeInCycleGenerationRef.current;
    if (!bargeInPausedRef.current) {
      cycle += 1;
      bargeInCycleGenerationRef.current = cycle;
      bargeInOwnerRef.current = captureVoiceTurnOwner({
        generationId: liveVoiceGenerationRef.current,
        voiceSessionId: voiceSessionIdRef.current,
        conversationId: activeThreadIdRef.current,
        interruptionCandidateId: cycle,
        sourceState: 'speaking',
        expectedStates: ['interruption_check', 'listening'],
      });
      if (!voiceIncidentRef.current) voiceIncidentRef.current = createVoiceIncidentRef();
      // During the brief acoustic check, neither speaker owns the caption.
      // Recognized user speech appears from onresult; a rejected interruption
      // restores Sonny's exact response in resumeAfterFalseBargeIn.
      setLiveTranscript('');
      setAssistantPreview('');
      setLiveVoiceState('interruption_check');
      liveVoiceStateRef.current = 'interruption_check';
      const pausedServerVoice = pauseBrightResolveVoice();
      if (!pausedServerVoice) {
        try { window.speechSynthesis?.pause?.(); } catch (_) {
          bargeInOwnerRef.current = null;
          setLiveVoiceState('speaking');
          liveVoiceStateRef.current = 'speaking';
          setAssistantPreview(recentAssistantSpeechRef.current.text || '');
          return false;
        }
      }
      bargeInPausedRef.current = true;
      bargeInCycleOpenRef.current = true;
      traceVoiceIncident('playback_paused', {
        playback_state: 'paused',
        owner_valid: true,
      });
    }
    if (Number.isFinite(Number(recoveryMs)) && Number(recoveryMs) > 0) {
      // Re-arm a bounded fail-safe whenever sound continues. This protects
      // browsers that emit acoustic activity but omit speechend/final events.
      bargeInDecisionTimerRef.current = rearmBargeInRecovery({
        currentTimer: bargeInDecisionTimerRef.current,
        clearTimer: window.clearTimeout.bind(window),
        scheduleTimer: window.setTimeout.bind(window),
        onRecover: () => {
          if (cycle !== bargeInCycleGenerationRef.current) return;
          // This timer represents a bounded quiet window after the latest
          // recognition update. Chrome can omit speechend while leaving the
          // advisory speech-start flag set; clear that stale flag so the
          // existing final-grace classifier can settle instead of waiting for
          // the 30-second absolute utterance ceiling.
          bargeInSpeechActiveRef.current = false;
          setOrbSpeechActive(false);
          settleBargeInAfterSpeechEnd(
            recentAssistantSpeechRef.current.text || '',
            cycle,
          );
        },
        recoveryMs,
        minimumMs: BARGE_IN_FALSE_ALARM_RECOVERY_MS,
      });
    } else {
      if (bargeInDecisionTimerRef.current) clearTimeout(bargeInDecisionTimerRef.current);
      bargeInDecisionTimerRef.current = null;
    }
    return true;
  };

  const stopImmediateBargeInActivityMonitor = () => {
    if (bargeInActivityTimerRef.current) {
      window.clearInterval(bargeInActivityTimerRef.current);
      bargeInActivityTimerRef.current = null;
    }
  };

  const startImmediateBargeInActivityMonitor = () => {
    stopImmediateBargeInActivityMonitor();
    const analyser = analyserRef.current;
    if (!analyser) return false;

    let noiseFloor = 0.008;
    let activeFrames = 0;
    let warmupFrames = 0;
    bargeInActivityTimerRef.current = window.setInterval(() => {
      if (
        !bargeInActiveRef.current ||
        !isLiveOpenRef.current ||
        isMutedRef.current ||
        liveVoiceStateRef.current !== 'speaking'
      ) {
        stopImmediateBargeInActivityMonitor();
        return;
      }

      const samples = new Uint8Array(analyser.fftSize);
      analyser.getByteTimeDomainData(samples);
      let energy = 0;
      let peak = 0;
      for (let index = 0; index < samples.length; index += 1) {
        const normalized = (samples[index] - 128) / 128;
        energy += normalized * normalized;
        peak = Math.max(peak, Math.abs(normalized));
      }
      const rms = Math.sqrt(energy / samples.length);

      // Briefly learn the echo-cancelled playback floor before acting. This
      // prevents Sonny's own first syllable from pausing his audio while the
      // recognition path remains available for a very early spoken barge-in.
      if (warmupFrames < 5) {
        noiseFloor = noiseFloor * 0.7 + rms * 0.3;
        warmupFrames += 1;
        return;
      }

      const activity = evaluateRecordedVoiceActivity({
        rms,
        peak,
        noiseFloor,
        consecutiveActiveFrames: activeFrames,
      });
      activeFrames = activity.nextConsecutiveActiveFrames;
      if (!activity.active) noiseFloor = noiseFloor * 0.96 + rms * 0.04;
      if (activity.confirmed) {
        // This is a non-recording acoustic yield. Web Speech still performs
        // transcript/echo classification before anything is submitted.
        // Raw room energy gets one bounded yield, not an indefinitely re-armed
        // pause. Recognized speech independently re-arms recovery in onresult.
        if (
          !bargeInPausedRef.current &&
          bargeInSpeechActiveRef.current &&
          bargeInPendingRef.current?.candidate &&
          Date.now() >= bargeInAcousticCooldownUntilRef.current
        ) {
          pauseForPotentialBargeIn(BARGE_IN_ACOUSTIC_RECOVERY_MS);
        }
      }
    }, 40);
    return true;
  };

  const acceptVoiceBargeIn = async (candidate) => {
    const accepted = String(candidate || '').trim();
    if (!accepted) return false;
    const candidateId = bargeInCycleGenerationRef.current;
    if (
      bargeInPausedRef.current &&
      !isCurrentBargeInOwner(candidateId, ['interruption_check', 'listening'])
    ) {
      traceVoiceIncident('candidate_rejected', {
        playback_state: 'paused',
        classifier_action: 'reject',
        classifier_reason: 'stale_owner',
        owner_valid: false,
      });
      voiceIncidentRef.current = '';
      return false;
    }
    traceVoiceIncident('candidate_accepted', {
      playback_state: bargeInPausedRef.current ? 'paused' : 'active',
      classifier_action: 'interrupt',
      classifier_reason: 'accepted_replacement',
      owner_valid: true,
    });
    activeDeliveryTrackerRef.current?.emit('interrupted', {
      playbackRef: activePlaybackEvidenceRef.current,
    });
    activeDeliveryTrackerRef.current = null;
    activePlaybackEvidenceRef.current = '';
    voiceLatencyRef.current.mark('interruption_confirmed');
    bargeInInterruptedRef.current = true;
    bargeInCycleGenerationRef.current += 1;
    bargeInOwnerRef.current = null;
    bargeInActiveRef.current = false;
    stopImmediateBargeInActivityMonitor();
    bargeInPausedRef.current = false;
    bargeInPendingRef.current = null;
    bargeInCycleOpenRef.current = false;
    bargeInSpeechActiveRef.current = false;
    bargeInIgnoreResultsUntilRef.current = 0;
    bargeInAcousticCooldownUntilRef.current = 0;
    abortVoiceTurn(liveRequestAbortRef);
    advanceVoiceGeneration(liveVoiceGenerationRef);
    traceVoiceLifecycle('old_turn_invalidated', {
      interruption_candidate_id: candidateId,
    });
    if (bargeInTimerRef.current) clearTimeout(bargeInTimerRef.current);
    if (bargeInDecisionTimerRef.current) clearTimeout(bargeInDecisionTimerRef.current);
    bargeInDecisionTimerRef.current = null;
    stopRecognition();
    cancelBrightResolveVoice();
    try { window.speechSynthesis?.cancel?.(); } catch (_) {}
    setIsRecording(false);
    setAssistantPreview('');
    setLiveTranscript(accepted);
    setLiveVoiceState('listening');
    liveVoiceStateRef.current = 'listening';
    if (isControlOnlyVoiceCommand(accepted)) {
      // Stop/yield commands hand the floor back without manufacturing a model
      // turn from the command or any stale topic in conversation history.
      setLiveTranscript('');
      voiceIncidentRef.current = '';
      if (isLiveOpenRef.current && !isMutedRef.current) restartLiveRecognition();
      return true;
    }
    await submitPrompt(accepted, {
      live: true,
      keepComposerText: true,
      voiceInterruption: true,
      voiceIncidentRef: voiceIncidentRef.current,
    });
    return true;
  };

  const settleBargeInAfterSpeechEnd = (
    assistantText,
    expectedCycle = bargeInCycleGenerationRef.current,
  ) => {
    if (expectedCycle !== bargeInCycleGenerationRef.current) return;
    if (!isCurrentBargeInOwner(expectedCycle, ['interruption_check', 'listening'])) return;
    if (
      !bargeInPausedRef.current || !bargeInCycleOpenRef.current ||
      bargeInSpeechActiveRef.current || bargeInInterruptedRef.current
    ) return;
    if (bargeInDecisionTimerRef.current) clearTimeout(bargeInDecisionTimerRef.current);
    const settledPending = bargeInPendingRef.current;
    // A final result has already crossed the recognizer's phrase boundary and
    // can be classified now. Likewise, an acoustic-only false alarm has no
    // transcript that another recognition cycle could improve. Do not stack
    // the final-grace timer on either rejected path; resume the same playback
    // position in this task. Only an unresolved interim keeps the grace window.
    if (!settledPending?.candidate) {
      bargeInDecisionTimerRef.current = null;
      resumeAfterFalseBargeIn(expectedCycle);
      return;
    }
    if (settledPending.isFinal === true) {
      const settledDecision = decideVoiceBargeIn({
        candidate: settledPending.candidate,
        assistantText,
        confidence: settledPending.confidence,
        isFinal: true,
        playbackPaused: true,
      });
      bargeInDecisionTimerRef.current = null;
      if (settledDecision.action === 'interrupt') {
        void acceptVoiceBargeIn(settledPending.candidate);
      } else {
        resumeAfterFalseBargeIn(expectedCycle);
      }
      return;
    }
    bargeInDecisionTimerRef.current = window.setTimeout(async () => {
      if (expectedCycle !== bargeInCycleGenerationRef.current) return;
      if (!isCurrentBargeInOwner(expectedCycle, ['interruption_check', 'listening'])) return;
      bargeInDecisionTimerRef.current = null;
      if (
        !bargeInPausedRef.current || !bargeInActiveRef.current ||
        bargeInInterruptedRef.current || bargeInSpeechActiveRef.current
      ) {
        return;
      }
      const pending = bargeInPendingRef.current;
      if (pending?.candidate) {
        observeEphemeralVoiceCandidate({
          queue: voiceObservationQueueRef.current,
          surface: 'public',
          candidate: pending.candidate,
          assistantText,
          confidence: pending.confidence,
          isFinal: true,
        });
        const decision = decideVoiceBargeIn({
          candidate: pending.candidate,
          assistantText,
          confidence: pending.confidence,
          isFinal: true,
          playbackPaused: true,
        });
        if (decision.action === 'interrupt') {
          await acceptVoiceBargeIn(pending.candidate);
          return;
        }
      }
      resumeAfterFalseBargeIn(expectedCycle);
    }, BARGE_IN_FINAL_GRACE_MS);
  };

  const startBargeInRecognition = (assistantText) => {
    if (!supportsDuplexVoiceMonitoring() || !isLiveOpenRef.current || isMutedRef.current) return false;
    const recognition = buildRecognition();
    if (!recognition) return false;

    bargeInActiveRef.current = true;
    const utteranceGuard = createVoiceUtteranceGuard({
      maximumMs: VOICE_MAX_UTTERANCE_MS,
      onMaximum: () => {
        if (recognitionRef.current !== recognition || bargeInInterruptedRef.current) return;
        bargeInSpeechActiveRef.current = false;
        setOrbSpeechActive(false);
        voiceLatencyRef.current.mark('utterance_maximum');
        try { recognition.stop(); } catch {
          // A concurrently ending recognizer is already on the same bounded
          // settlement path; no content or provider detail is logged here.
        }
        if (bargeInPausedRef.current) settleBargeInAfterSpeechEnd(assistantText);
      },
    });

    recognition.onstart = () => {
      setIsRecording(true);
      traceVoiceLifecycleIncident('recognition_started', {
        recognition_state: 'active',
        interruption_mode: 'duplex',
      });
    };

    recognition.onspeechstart = () => {
      if (
        recognitionRef.current !== recognition ||
        !isLiveOpenRef.current ||
        isMutedRef.current ||
        !['speaking', 'listening', 'interruption_check'].includes(liveVoiceStateRef.current)
      ) return;
      // The current recognizer is the lifecycle authority. A playback or
      // recovery callback may have cleared the advisory active flag, but it
      // must never make a still-current recognizer discard the next turn.
      bargeInActiveRef.current = true;
      if (!voiceIncidentRef.current) voiceIncidentRef.current = createVoiceIncidentRef();
      bargeInInterruptedRef.current = false;
      bargeInCycleOpenRef.current = true;
      bargeInSpeechActiveRef.current = true;
      setOrbSpeechActive(false);
      utteranceGuard.start();
      bargeInPendingRef.current = null;
      bargeInIgnoreResultsUntilRef.current = 0;
      voiceLatencyRef.current.beginIfNeeded();
      voiceLatencyRef.current.mark('speech_started');
      voiceLatencyRef.current.mark('interruption_candidate');
      traceVoiceIncident('candidate_started', {
        playback_state: 'active',
        recognition_state: 'speech_start',
          microphone_energy_bucket: summarizeVoiceIncidentEvidence({
            microphoneEnergy: latestMicrophoneEnergyRef.current,
          }).microphone_energy_bucket,
      });
      // Chrome's native speech boundary is the earliest ownership evidence.
      // Yield before its interim transcript can be contaminated by continued
      // assistant playback; final transcript classification still owns
      // acceptance versus exact-position false-alarm recovery.
      pauseForPotentialBargeIn(BARGE_IN_ACOUSTIC_RECOVERY_MS);
    };

    recognition.onspeechend = () => {
      if (
        recognitionRef.current !== recognition ||
        bargeInInterruptedRef.current
      ) return;
      bargeInSpeechActiveRef.current = false;
      setOrbSpeechActive(false);
      utteranceGuard.finish();
      voiceLatencyRef.current.mark('speech_ended');
      settleBargeInAfterSpeechEnd(assistantText);
    };

    recognition.onresult = async (event) => {
      if (
        recognitionRef.current !== recognition ||
        !isLiveOpenRef.current ||
        isMutedRef.current ||
        !['speaking', 'listening', 'interruption_check'].includes(liveVoiceStateRef.current)
      ) return;

      // A result delivered by the current recognition session is valid even
      // if an unrelated playback completion briefly cleared the advisory
      // flag. Reassert ownership before classifying the transcript.
      bargeInActiveRef.current = true;

      // Chrome can split one spoken interruption across several result slots
      // and finalize those slots independently. Classifying only resultIndex
      // lets an early fragment resume playback before the rest of the same
      // utterance arrives. Rebuild the complete recognition snapshot so one
      // spoken floor claim has one pause/accept/reject decision.
      const snapshot = recognitionResultSnapshot(event);
      const candidate = snapshot.candidate;
      const isFinalResult = snapshot.hasFinal && !snapshot.hasInterim;
      const confidence = snapshot.confidence;
      if (candidate) {
        if (
          !bargeInCycleOpenRef.current &&
          Date.now() < bargeInIgnoreResultsUntilRef.current &&
          isFinalResult
        ) return;
        // Continuous recognition engines may omit speechstart between nearby
        // phrases. Never quarantine a fresh interim transcript: it is the
        // earliest reliable evidence of a new turn and must pause playback.
        // Only a late final from the closed cycle is quarantined above.
        if (!bargeInCycleOpenRef.current && candidate) {
          bargeInCycleOpenRef.current = true;
          utteranceGuard.start();
        }
        observeEphemeralVoiceCandidate({
          queue: voiceObservationQueueRef.current,
          surface: 'public',
          candidate,
          assistantText,
          confidence,
          isFinal: isFinalResult,
        });
        const decision = decideVoiceBargeIn({
          candidate,
          assistantText,
          confidence,
          isFinal: isFinalResult,
          playbackPaused: bargeInPausedRef.current,
        });
        traceVoiceIncident('candidate_classified', {
          playback_state: bargeInPausedRef.current ? 'paused' : 'active',
          ...summarizeVoiceIncidentEvidence({
            candidate,
            assistantText,
            confidence,
            isFinal: isFinalResult,
            microphoneEnergy: latestMicrophoneEnergyRef.current,
          }),
          classifier_action: decision.action,
          classifier_reason: decision.reason,
          owner_valid: bargeInPausedRef.current
            ? isCurrentBargeInOwner(bargeInCycleGenerationRef.current, ['interruption_check', 'listening'])
            : true,
        });
        // Recognition/VAD owns the speech decision. The orb consumes only
        // this presentation-facing boolean; amplitude still controls depth.
        setOrbSpeechActive(['pause', 'hold', 'interrupt'].includes(decision.action));

        if (decision.action === 'pause') {
          const presentCandidate = shouldPresentInterruptionCandidate({
            candidate,
            assistantText,
          });
          if (presentCandidate) {
            bargeInPendingRef.current = {
              candidate,
              confidence,
              isFinal: false,
            };
          }
          pauseForPotentialBargeIn(BARGE_IN_ACOUSTIC_RECOVERY_MS);
          // pauseForPotentialBargeIn intentionally clears the previous caption
          // before changing ownership. Restore the recognized user fragment in
          // the same browser task so LISTENING never presents stale Sonny text
          // or a blank caption after credible speech has already arrived.
          if (presentCandidate) setLiveTranscript(candidate);
          return;
        }
        if (decision.action === 'hold') {
          setLiveTranscript('');
          return;
        }
        // Assistant echo, uncertain fragments, filler, and incidental room
        // audio never enter history. They resume playback if a cautious
        // interim pause occurred while recognition was deciding.
        if (decision.action !== 'interrupt') {
          bargeInPendingRef.current = null;
          setOrbSpeechActive(false);
          setLiveTranscript('');
          // A final result is itself a phrase boundary. Some Web Speech
          // engines keep continuous recognition open and omit speechend, so
          // schedule deterministic false-alarm recovery here as well.
          if (
            isFinalResult && bargeInPausedRef.current &&
            !bargeInSpeechActiveRef.current
          ) {
            settleBargeInAfterSpeechEnd(assistantText);
          } else if (isFinalResult && !bargeInPausedRef.current) {
            traceVoiceIncident('candidate_rejected', {
              playback_state: 'active',
              classifier_action: decision.action,
              classifier_reason: decision.reason,
              owner_valid: true,
            });
            voiceIncidentRef.current = '';
          }
          return;
        }

        bargeInPendingRef.current = {
          candidate,
          confidence,
          isFinal: true,
        };
        await acceptVoiceBargeIn(candidate);
        return;
      }
    };

    recognition.onerror = (event) => {
      const error = event?.error || '';
      traceVoiceLifecycleIncident('recognition_error', {
        recognition_state: 'error',
        interruption_mode: 'duplex',
        invalidation_reason: ['aborted', 'no-speech', 'network', 'audio-capture'].includes(error)
          ? error.replace('-', '_')
          : 'other',
      });
      utteranceGuard.finish();
      if (recognitionRef.current === recognition) setOrbSpeechActive(false);
      if (!['aborted', 'no-speech'].includes(error)) {
        console.warn('[ChatShell] Duplex interruption monitoring unavailable.');
      }
    };

    recognition.onend = () => {
      traceVoiceLifecycleIncident('recognition_ended', {
        recognition_state: 'ended',
        interruption_mode: 'duplex',
      });
      utteranceGuard.finish();
      setIsRecording(false);
      // Do not resume here. Web Speech can cycle onend/onstart while a person
      // is mid-sentence; the trailing-silence recovery timer owns resumption.
      if (
        recognitionRef.current !== recognition ||
        !bargeInActiveRef.current ||
        bargeInInterruptedRef.current
      ) return;
      bargeInSpeechActiveRef.current = false;
      setOrbSpeechActive(false);
      if (bargeInPausedRef.current) settleBargeInAfterSpeechEnd(assistantText);
      bargeInTimerRef.current = window.setTimeout(() => {
        if (
          bargeInActiveRef.current &&
          isLiveOpenRef.current &&
          !isMutedRef.current &&
          liveVoiceStateRef.current === 'speaking'
        ) startBargeInRecognition(assistantText);
      }, 20);
    };

    try {
      recognition.start();
      return true;
    } catch (_) {
      utteranceGuard.cancel();
      bargeInActiveRef.current = false;
      return false;
    }
  };

  const startRecordedBargeInRecognition = async (assistantText) => {
    if (
      (!isAppleMobileVoiceRuntime() && getSpeechRecognitionCtor()) ||
      typeof window === 'undefined' ||
      typeof window.MediaRecorder !== 'function' ||
      !isLiveOpenRef.current ||
      isMutedRef.current ||
      liveVoiceStateRef.current !== 'speaking'
    ) return false;

    const meterReady = await startAudioMeter();
    if (!meterReady && micPermissionDeniedRef.current) return false;
    const stream = mediaStreamRef.current;
    if (!stream || liveVoiceStateRef.current !== 'speaking') return false;

    stopRecognition();
    let capture;
    try {
      capture = createRecordedVoiceCapture({
        stream,
        analyser: analyserRef.current,
        MediaRecorderCtor: window.MediaRecorder,
        onStage: (stage, detail) => {
          reportRecordedVoiceStage(stage, detail);
          const lifecycleStage = stage === 'recording_started'
            ? 'recorded_capture_started'
            : stage === 'recording_stopped'
              ? 'recorded_capture_ended'
              : ['recorder_error', 'capture_failed'].includes(stage)
                ? 'recorded_capture_error'
                : null;
          if (lifecycleStage) {
            traceVoiceLifecycleIncident(lifecycleStage, {
              recognition_state: lifecycleStage.endsWith('error') ? 'error' : 'recorded',
              interruption_mode: 'recorded',
              invalidation_reason: lifecycleStage.endsWith('error') ? 'capture_failed' : 'unavailable',
            });
          }
        },
        onSpeechStart: () => {
          if (!voiceIncidentRef.current) voiceIncidentRef.current = createVoiceIncidentRef();
          traceVoiceIncident('candidate_started', {
            playback_state: 'active',
            recognition_state: 'speech_start',
            microphone_energy_bucket: summarizeVoiceIncidentEvidence({
              microphoneEnergy: latestMicrophoneEnergyRef.current,
            }).microphone_energy_bucket,
          });
          pauseForPotentialBargeIn(6000);
        },
        discardBeforeSpeech: true,
        hardTimeoutMs: 15000,
        minSpeechMs: 220,
        silenceMs: 650,
      });
    } catch (_) {
      return false;
    }

    const sequence = recordedBargeInSequenceRef.current + 1;
    recordedBargeInSequenceRef.current = sequence;
    recordedBargeInDiscardRef.current = false;
    const recognitionHandle = {
      stop: () => capture.stop('ui_stop'),
      abort: () => capture.cancel(),
    };
    recordedBargeInRecorderRef.current = recognitionHandle;
    recognitionRef.current = recognitionHandle;
    bargeInActiveRef.current = true;
    const restartIfStillSpeaking = () => {
      if (
        sequence === recordedBargeInSequenceRef.current &&
        isLiveOpenRef.current &&
        !isMutedRef.current &&
        !bargeInInterruptedRef.current &&
        liveVoiceStateRef.current === 'speaking'
      ) {
        window.setTimeout(() => startRecordedBargeInRecognition(assistantText), 120);
      }
    };

    setIsRecording(true);
    capture.done.then(async ({ blob, heardSpeech }) => {
      recognitionRef.current = null;
      if (recordedBargeInRecorderRef.current === recognitionHandle) recordedBargeInRecorderRef.current = null;
      setIsRecording(false);

      const discard = recordedBargeInDiscardRef.current ||
        sequence !== recordedBargeInSequenceRef.current ||
        !heardSpeech ||
        !isLiveOpenRef.current ||
        isMutedRef.current;
      if (discard) {
        resumeAfterFalseBargeIn(bargeInCycleGenerationRef.current);
        restartIfStillSpeaking();
        return;
      }

      if (!blob.size || blob.size > 4 * 1024 * 1024) {
        resumeAfterFalseBargeIn(bargeInCycleGenerationRef.current);
        restartIfStillSpeaking();
        return;
      }

      try {
        // Keep the recovery fail-safe alive while bounded audio is transcribed.
        pauseForPotentialBargeIn(6000);
        const response = await withCreds('/api/voice/transcribe', {
          method: 'POST',
          headers: { 'Content-Type': blob.type || 'application/octet-stream' },
          body: blob,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error('transcription_failed');
        const candidate = String(payload.text || '').trim();
        observeEphemeralVoiceCandidate({
          queue: voiceObservationQueueRef.current,
          surface: 'public',
          candidate,
          assistantText,
          confidence: payload.confidence,
          isFinal: true,
        });
        const decision = decideVoiceBargeIn({
          candidate,
          assistantText,
          confidence: payload.confidence,
          isFinal: true,
          playbackPaused: bargeInPausedRef.current,
        });
        traceVoiceIncident('candidate_classified', {
          playback_state: bargeInPausedRef.current ? 'paused' : 'active',
          ...summarizeVoiceIncidentEvidence({
            candidate,
            assistantText,
            confidence: payload.confidence,
            isFinal: true,
            microphoneEnergy: latestMicrophoneEnergyRef.current,
          }),
          classifier_action: decision.action,
          classifier_reason: decision.reason,
          owner_valid: isCurrentBargeInOwner(
            bargeInCycleGenerationRef.current,
            ['interruption_check', 'listening'],
          ),
        });
        if (decision.action === 'interrupt') {
          await acceptVoiceBargeIn(candidate);
          return;
        }
      } catch (_) {
        // Playback monitoring is opportunistic. Provider/network details stay
        // private and a failed or rejected fragment never enters chat history.
      }
      resumeAfterFalseBargeIn(bargeInCycleGenerationRef.current);
      restartIfStillSpeaking();
    }).catch((captureError) => {
      setIsRecording(false);
      if (captureError?.name === 'AbortError') return;
      recordedBargeInDiscardRef.current = true;
      resumeAfterFalseBargeIn(bargeInCycleGenerationRef.current);
      restartIfStillSpeaking();
    });
    return true;
  };

  const startVoiceBargeInMonitoring = async (assistantText) => {
    const mode = getDuplexVoiceMonitoringMode();
    if (mode === 'web_speech') return startBargeInRecognition(assistantText);
    if (mode === 'recorded') {
      const recordedStarted = await startRecordedBargeInRecognition(assistantText);
      if (recordedStarted) return true;
      if (
        getSpeechRecognitionCtor() &&
        isLiveOpenRef.current &&
        !isMutedRef.current &&
        liveVoiceStateRef.current === 'speaking'
      ) {
        return startBargeInRecognition(assistantText);
      }
    }
    return false;
  };

  const enterThinkingInterruptionCheck = (generation) => {
    if (
      !isCurrentVoiceGeneration(liveVoiceGenerationRef, generation) ||
      livePendingGenerationRef.current !== generation ||
      !['thinking', 'interruption_check'].includes(liveVoiceStateRef.current)
    ) return false;
    if (liveVoiceStateRef.current !== 'interruption_check') {
      const candidateId = thinkingInterruptionSequenceRef.current + 1;
      thinkingInterruptionSequenceRef.current = candidateId;
      thinkingInterruptionOwnerRef.current = captureVoiceTurnOwner({
        generationId: generation,
        voiceSessionId: voiceSessionIdRef.current,
        conversationId: activeThreadIdRef.current,
        interruptionCandidateId: candidateId,
        sourceState: 'thinking',
        expectedStates: ['interruption_check'],
      });
    }
    setLiveVoiceState('interruption_check');
    liveVoiceStateRef.current = 'interruption_check';
    return true;
  };

  const restoreThinkingAfterFalseInterruption = (generation) => {
    const owner = thinkingInterruptionOwnerRef.current;
    if (
      liveVoiceStateRef.current !== 'interruption_check' ||
      !isCurrentVoiceGeneration(liveVoiceGenerationRef, generation) ||
      livePendingGenerationRef.current !== generation ||
      !owner ||
      !isCurrentVoiceTurnOwner(owner, {
        generationId: generation,
        voiceSessionId: voiceSessionIdRef.current,
        conversationId: activeThreadIdRef.current,
        interruptionCandidateId: owner.interruptionCandidateId,
        sourceState: 'thinking',
        state: 'interruption_check',
      })
    ) return false;
    thinkingInterruptionOwnerRef.current = null;
    setLiveTranscript(pendingLiveUserTranscriptRef.current);
    setLiveVoiceState('thinking');
    liveVoiceStateRef.current = 'thinking';
    return true;
  };

  const startRecordedRecognition = async ({ whileThinking = false, generation = null } = {}) => {
    if (typeof window === 'undefined' || typeof window.MediaRecorder !== 'function') {
      setError('Voice input is not available in this browser.');
      return false;
    }
    const meterReady = await startAudioMeter();
    if (!meterReady && micPermissionDeniedRef.current) return false;
    const stream = mediaStreamRef.current;
    if (!stream) return false;
    let capture;
    let captureGeneration = generation ?? liveVoiceGenerationRef.current;
    try {
      capture = createRecordedVoiceCapture({
        stream,
        analyser: analyserRef.current,
        MediaRecorderCtor: window.MediaRecorder,
        onStage: (stage, detail) => {
          reportRecordedVoiceStage(stage, detail);
          const lifecycleStage = stage === 'recording_started'
            ? 'recorded_capture_started'
            : stage === 'recording_stopped'
              ? 'recorded_capture_ended'
              : ['recorder_error', 'capture_failed'].includes(stage)
                ? 'recorded_capture_error'
                : null;
          if (lifecycleStage) {
            traceVoiceLifecycleIncident(lifecycleStage, {
              recognition_state: lifecycleStage.endsWith('error') ? 'error' : 'recorded',
              interruption_mode: whileThinking ? 'thinking_recorded' : 'recorded',
              invalidation_reason: lifecycleStage.endsWith('error') ? 'capture_failed' : 'unavailable',
            });
          }
        },
        discardBeforeSpeech: !whileThinking && Boolean(analyserRef.current),
        hardTimeoutMs: analyserRef.current ? 45000 : 12000,
        silenceMs: 950,
        // Acoustic energy makes the UI attentive, but it is not enough to
        // cancel a model request. Cancellation happens only after the bounded
        // recording is transcribed and passes replacement classification.
        onSpeechStart: () => {
          if (whileThinking) enterThinkingInterruptionCheck(captureGeneration);
        },
      });
    } catch (_) {
      reportRecordedVoiceStage('recorder_create_failed');
      return false;
    }
    recognitionRef.current = {
      stop: () => capture.stop('ui_stop'),
      abort: () => capture.cancel(),
    };
    setIsRecording(true);
    if (voiceSessionOpeningRef.current) {
      traceVoiceLifecycle('recognition_ready', { mode: 'recorded' });
      voiceSessionOpeningRef.current = null;
    }
    if (!whileThinking) {
      setLiveVoiceState('listening');
      liveVoiceStateRef.current = 'listening';
    }
    setLiveTranscript(whileThinking ? pendingLiveUserTranscriptRef.current : '');
    capture.done.then(async ({ blob, heardSpeech }) => {
      setIsRecording(false);
      recognitionRef.current = null;
      if (!isLiveOpenRef.current || isMutedRef.current || !isCurrentVoiceGeneration(liveVoiceGenerationRef, captureGeneration)) return;
      // Release a completed iPhone/iPad turn before network transcription and
      // response generation. The route can settle while the request is in
      // flight instead of switching audibly at the instant Sonny starts.
      if (heardSpeech && isAppleMobileVoiceRuntime()) releaseLiveInputAudio();
      // Thinking-time awareness may replace an obsolete response only after
      // the local analyser confirms new speech. Recorder timeouts can create
      // non-empty room-tone blobs that must never become duplicate turns.
      if (whileThinking && !heardSpeech) {
        restoreThinkingAfterFalseInterruption(captureGeneration);
        reportRecordedVoiceStage('no_speech_restart');
        window.setTimeout(() => startLiveRecognition({
          whileThinking: true,
          generation: captureGeneration,
        }), 120);
        return;
      }
      if (!blob.size || blob.size > 4 * 1024 * 1024) {
        reportRecordedVoiceStage('audio_rejected', { reason: blob.size ? 'size' : 'empty' });
        if (isLiveOpenRef.current && !isMutedRef.current) {
          window.setTimeout(() => startLiveRecognition({
            whileThinking,
            generation: captureGeneration,
          }), 120);
        }
        return;
      }
      try {
        reportRecordedVoiceStage('upload_started');
        const response = await withCreds('/api/voice/transcribe', {
          method: 'POST', headers: { 'Content-Type': blob.type || 'application/octet-stream' }, body: blob,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error('transcription_failed');
        const text = String(payload.text || '').trim();
        if (text) {
          reportRecordedVoiceStage('transcript_received');
          const ordinaryDecision = classifyOrdinaryVoiceTranscript({
            candidate: text,
            confidence: payload.confidence,
          });
          if (!whileThinking && ordinaryDecision.action !== 'accept') {
            reportRecordedVoiceStage('transcript_rejected', { reason: ordinaryDecision.reason });
            setLiveTranscript('');
            if (isLiveOpenRef.current && !isMutedRef.current) {
              window.setTimeout(startLiveRecognition, 120);
            }
            return;
          }
          const recentSpeech = recentAssistantSpeechRef.current;
          if (shouldIgnoreRecentAssistantEcho({
            candidate: text,
            assistantText: recentSpeech.text,
            elapsedMs: Date.now() - recentSpeech.endedAt,
            cooldownMs: ASSISTANT_ECHO_COOLDOWN_MS,
          })) {
            setLiveTranscript('');
            if (whileThinking) restoreThinkingAfterFalseInterruption(captureGeneration);
            if (isLiveOpenRef.current && !isMutedRef.current) {
              window.setTimeout(startLiveRecognition, LIVE_FINAL_DEBOUNCE_MS);
            }
            return;
          }
          if (whileThinking) {
            const replacement = classifyThinkingTurnReplacement({
              candidate: text,
              assistantText: recentSpeech.text,
              confidence: payload.confidence,
              isFinal: true,
            });
            if (replacement.action !== 'replace') {
              restoreThinkingAfterFalseInterruption(captureGeneration);
              window.setTimeout(() => startLiveRecognition({
                whileThinking: true,
                generation: captureGeneration,
              }), 120);
              return;
            }
            if (isControlOnlyVoiceCommand(text)) {
              abortVoiceTurn(liveRequestAbortRef);
              if (livePendingGenerationRef.current === captureGeneration) {
                livePendingGenerationRef.current = null;
                setIsSending(false);
              }
              captureGeneration = advanceVoiceGeneration(liveVoiceGenerationRef);
              voiceLatencyRef.current.cancel();
              setLiveTranscript('');
              setLiveVoiceState('listening');
              liveVoiceStateRef.current = 'listening';
              window.setTimeout(startLiveRecognition, 120);
              return;
            }
          }
          setLiveTranscript(text);
          await submitPrompt(text, {
            live: true,
            keepComposerText: true,
            replaceActiveLiveTurn: whileThinking,
            voiceInterruption: whileThinking,
          });
          return;
        }
      } catch (_) {
        reportRecordedVoiceStage('transcription_failed');
        setError('Live transcription is temporarily unavailable in this browser.');
      }
      // Empty/no-speech clips are expected in quiet rooms; restart without an
      // error. This also prevents Firefox from appearing frozen after its
      // bounded recorder deadline.
      if (!heardSpeech) reportRecordedVoiceStage('no_speech_restart');
      if (whileThinking) restoreThinkingAfterFalseInterruption(captureGeneration);
      if (isLiveOpenRef.current && !isMutedRef.current) {
        window.setTimeout(() => startLiveRecognition({
          whileThinking,
          generation: captureGeneration,
        }), 120);
      }
    }).catch((captureError) => {
      setIsRecording(false);
      recognitionRef.current = null;
      if (captureError?.name === 'AbortError') return;
      reportRecordedVoiceStage('capture_failed');
      if (isLiveOpenRef.current && !isMutedRef.current) {
        setLiveVoiceState('error');
        liveVoiceStateRef.current = 'error';
        setError('Live voice input failed before transcription.');
      }
    });
    return true;
  };

  const startLiveRecognition = async ({ whileThinking = false, generation = null } = {}) => {
    // Playback-only mode improves iOS speaker routing, but must be released
    // before the next microphone acquisition.
    setBrightResolveAudioSessionType('auto');
    // Capable mobile and desktop browsers share the same interim-caption
    // path. The recorder is retained only when recognition is unavailable or
    // its service fails, avoiding the previous whole-clip upload delay.
    if (!getSpeechRecognitionCtor()) {
      const started = await startRecordedRecognition({ whileThinking, generation });
      if (!started) {
        setLiveVoiceState('error');
        liveVoiceStateRef.current = 'error';
        setError('Voice input is not available in this browser.');
      }
      return;
    }
    const recognition = buildRecognition();
    if (!recognition) {
      setLiveVoiceState('error');
      liveVoiceStateRef.current = 'error';
      return;
    }

    const meterReady = await startAudioMeter();
    if (!meterReady && micPermissionDeniedRef.current) return;
    // Another lifecycle transition may have stopped or replaced this
    // recognizer while microphone setup was awaiting permission. A stale
    // recognizer must never reclaim the live session.
    if (recognitionRef.current !== recognition) return;
    const recognitionGeneration = recognition.__brightResolveGenerationId;
    const recognitionVoiceSessionId = voiceSessionIdRef.current;
    const recognitionConversationId = activeThreadIdRef.current;
    const isCurrentRecognitionOwner = () => (
      recognitionRef.current === recognition &&
      recognitionGenerationRef.current === recognitionGeneration &&
      voiceSessionIdRef.current === recognitionVoiceSessionId &&
      activeThreadIdRef.current === recognitionConversationId
    );
    setLiveTranscript(whileThinking ? pendingLiveUserTranscriptRef.current : '');
    liveFinalBufferRef.current = '';
    let thinkingCandidate = '';
    let thinkingCandidateConfidence = 0;
    let thinkingTurnClaimed = false;
    let latestLiveCandidate = '';
    let hasUnsettledInterim = false;
    let liveFinalizationClaimed = false;
    // A result can race the handoff from the ordinary listener to the
    // thinking-time listener. Adopt the pending generation in place so that
    // recognized speech is never displayed and then discarded merely because
    // it arrived on the recognizer that was active one browser task earlier.
    let replacingPendingTurn = whileThinking;
    let ownedThinkingGeneration = generation;
    const adoptPendingTurnIfNeeded = () => {
      const pendingGeneration = livePendingGenerationRef.current;
      if (
        !replacingPendingTurn && pendingGeneration !== null &&
        ['thinking', 'interruption_check'].includes(liveVoiceStateRef.current)
      ) {
        replacingPendingTurn = true;
        ownedThinkingGeneration = pendingGeneration;
      }
      return replacingPendingTurn;
    };

    const finalizeRecognizedTurn = async (reason = 'final') => {
      traceVoiceLifecycle('utterance_finalize_requested', { reason });
      if (
        liveFinalizationClaimed ||
        !isCurrentRecognitionOwner() ||
        !isLiveOpenRef.current ||
        isMutedRef.current
      ) {
        if (!isCurrentRecognitionOwner()) {
          traceVoiceLifecycle('stale_callback_ignored', { reason, callback: 'utterance_finalize' });
        }
        return false;
      }

      const allowStableInterim = ['silence', 'maximum', 'recognition_end', 'recovery']
        .includes(reason);
      const finalText = finalizedVoiceTranscript({
        finalTranscript: liveFinalBufferRef.current.trim() || thinkingCandidate,
        stableTranscript: latestLiveCandidate,
        hasInterim: hasUnsettledInterim,
        allowStableInterim,
      });
      if (!finalText) {
        if (replacingPendingTurn && ['silence', 'maximum'].includes(reason)) {
          liveFinalBufferRef.current = '';
          thinkingCandidate = '';
          latestLiveCandidate = '';
          hasUnsettledInterim = false;
          restoreThinkingAfterFalseInterruption(ownedThinkingGeneration);
        }
        return false;
      }

      if (!replacingPendingTurn) {
        const ordinaryDecision = classifyOrdinaryVoiceTranscript({
          candidate: finalText,
          confidence: thinkingCandidateConfidence,
        });
        if (ordinaryDecision.action !== 'accept') {
          liveFinalBufferRef.current = '';
          latestLiveCandidate = '';
          hasUnsettledInterim = false;
          setLiveTranscript('');
          return false;
        }
      }

      if (replacingPendingTurn) {
        const replacement = classifyThinkingTurnReplacement({
          candidate: finalText,
          assistantText: recentAssistantSpeechRef.current.text,
          confidence: thinkingCandidateConfidence,
          isFinal: true,
        });
        if (replacement.action !== 'replace') {
          liveFinalBufferRef.current = '';
          thinkingCandidate = '';
          latestLiveCandidate = '';
          restoreThinkingAfterFalseInterruption(ownedThinkingGeneration);
          return false;
        }
        thinkingInterruptionOwnerRef.current = null;
        if (isControlOnlyVoiceCommand(finalText)) {
          if (!thinkingTurnClaimed && isCurrentVoiceGeneration(
            liveVoiceGenerationRef,
            ownedThinkingGeneration,
          )) {
            thinkingTurnClaimed = true;
            abortVoiceTurn(liveRequestAbortRef);
            if (livePendingGenerationRef.current === ownedThinkingGeneration) {
              livePendingGenerationRef.current = null;
              setIsSending(false);
            }
            advanceVoiceGeneration(liveVoiceGenerationRef);
            voiceLatencyRef.current.cancel();
          }
          liveFinalizationClaimed = true;
          liveFinalBufferRef.current = '';
          thinkingCandidate = '';
          latestLiveCandidate = '';
          setLiveTranscript('');
          setLiveVoiceState('listening');
          liveVoiceStateRef.current = 'listening';
          stopRecognition();
          window.setTimeout(startLiveRecognition, 120);
          return true;
        }
        if (
          !thinkingTurnClaimed &&
          isCurrentVoiceGeneration(liveVoiceGenerationRef, ownedThinkingGeneration)
        ) {
          thinkingTurnClaimed = true;
          abortVoiceTurn(liveRequestAbortRef);
          if (livePendingGenerationRef.current === ownedThinkingGeneration) {
            livePendingGenerationRef.current = null;
            setIsSending(false);
          }
          advanceVoiceGeneration(liveVoiceGenerationRef);
          voiceLatencyRef.current.cancel();
          setLiveVoiceState('listening');
        }
      }

      // Floor-control phrases have no model content even when they arrive in
      // an ordinary listening window. A timing race must not turn "wait" into
      // a new chat prompt after the pending turn has already settled.
      if (!replacingPendingTurn && isControlOnlyVoiceCommand(finalText)) {
        liveFinalizationClaimed = true;
        liveFinalBufferRef.current = '';
        latestLiveCandidate = '';
        setLiveTranscript('');
        stopRecognition();
        setIsRecording(false);
        setLiveVoiceState('listening');
        liveVoiceStateRef.current = 'listening';
        window.setTimeout(startLiveRecognition, 120);
        return true;
      }

      const recentSpeech = recentAssistantSpeechRef.current;
      if (shouldIgnoreRecentAssistantEcho({
        candidate: finalText,
        assistantText: recentSpeech.text,
        elapsedMs: Date.now() - recentSpeech.endedAt,
        cooldownMs: ASSISTANT_ECHO_COOLDOWN_MS,
      })) {
        liveFinalBufferRef.current = '';
        thinkingCandidate = '';
        latestLiveCandidate = '';
        setLiveTranscript('');
        return false;
      }

      const currentBinding = voiceCanaryBindingRef.current;
      if (currentBinding) {
        if (!voiceCanaryBindingMatches(currentBinding, activeThreadIdRef.current)) {
          liveFinalizationClaimed = true;
          rejectVoiceCanaryBinding();
          return false;
        }
        const refreshed = await requestVoiceCanaryReadiness(activeThreadIdRef.current);
        if (
          refreshed.kind !== 'bound' ||
          !voiceCanaryReadinessMatchesBinding(currentBinding, refreshed.binding)
        ) {
          liveFinalizationClaimed = true;
          rejectVoiceCanaryBinding();
          return false;
        }
      }

      liveFinalizationClaimed = true;
      if (liveFinalTranscriptRecoveryRef.current) {
        window.clearTimeout(liveFinalTranscriptRecoveryRef.current);
        liveFinalTranscriptRecoveryRef.current = null;
      }
      liveFinalBufferRef.current = '';
      thinkingCandidate = '';
      latestLiveCandidate = '';
      stopRecognition();
      setIsRecording(false);
      setLiveTranscript(finalText);
      // Chromium may deliver a final result without firing onspeechend. Add a
      // fallback only when this speech cycle lacks that native boundary.
      voiceLatencyRef.current.markIfMissingAfter('speech_ended', 'speech_started');
      voiceLatencyRef.current.mark(`transcript_${reason}`);
      activeVoiceUserTurnIdRef.current = voiceUserTurnSequenceRef.current + 1;
      voiceUserTurnSequenceRef.current = activeVoiceUserTurnIdRef.current;
      traceVoiceLifecycle('utterance_finalized', { reason });

      console.timeStamp('[VOICE] submitting prompt');
      const watchdogOwner = {
        generationId: liveVoiceGenerationRef.current + 1,
        voiceSessionId: voiceSessionIdRef.current,
        conversationId: activeThreadIdRef.current,
      };
      liveProgressWatchdogRef.current.arm(watchdogOwner, (owner) => (
        isLiveOpenRef.current &&
        !isMutedRef.current &&
        owner.generationId === liveVoiceGenerationRef.current &&
        owner.voiceSessionId === voiceSessionIdRef.current &&
        owner.conversationId === activeThreadIdRef.current &&
        ['listening', 'transcribing', 'interruption_check'].includes(liveVoiceStateRef.current)
      ));
      await submitPrompt(finalText, {
        live: true,
        keepComposerText: true,
        replaceActiveLiveTurn: replacingPendingTurn,
        voiceInterruption: replacingPendingTurn,
        voiceUserTurnId: activeVoiceUserTurnIdRef.current,
      });
      return true;
    };

    const scheduleRecognizedTurnFinalization = (delayMs) => {
      if (liveSilenceTimerRef.current) clearTimeout(liveSilenceTimerRef.current);
      liveSilenceTimerRef.current = window.setTimeout(() => {
        liveSilenceTimerRef.current = null;
        void finalizeRecognizedTurn('silence');
      }, delayMs);
    };

    const scheduleFinalTranscriptRecovery = () => {
      if (liveFinalTranscriptRecoveryRef.current) {
        window.clearTimeout(liveFinalTranscriptRecoveryRef.current);
      }
      liveFinalTranscriptRecoveryRef.current = window.setTimeout(() => {
        liveFinalTranscriptRecoveryRef.current = null;
        if (
          liveFinalizationClaimed ||
          !isCurrentRecognitionOwner() ||
          !liveFinalBufferRef.current.trim() ||
          !['listening', 'interruption_check'].includes(liveVoiceStateRef.current)
        ) return;
        traceVoiceLifecycle('state_watchdog_triggered', {
          recovery: 'final_transcript_without_thinking',
        });
        void finalizeRecognizedTurn('recovery');
      }, Math.max(1500, LIVE_INTERIM_STALL_MS + 250));
    };

    const liveUtteranceGuard = createVoiceUtteranceGuard({
      maximumMs: VOICE_MAX_UTTERANCE_MS,
      onMaximum: () => {
        voiceLatencyRef.current.mark('utterance_maximum');
        void finalizeRecognizedTurn('maximum');
      },
    });

    recognition.onstart = () => {
      setIsRecording(true);
      traceVoiceLifecycleIncident('recognition_started', {
        recognition_state: 'active',
        interruption_mode: whileThinking ? 'thinking' : 'listening',
      });
      if (voiceSessionOpeningRef.current) {
        traceVoiceLifecycle('recognition_ready', { mode: 'web_speech' });
        voiceSessionOpeningRef.current = null;
      }
      if (!whileThinking) {
        setLiveVoiceState(isMutedRef.current ? 'muted' : 'listening');
        liveVoiceStateRef.current = isMutedRef.current ? 'muted' : 'listening';
      }
    };

    recognition.onspeechstart = () => {
      if (!isCurrentRecognitionOwner() || isMutedRef.current) return;
      setOrbSpeechActive(true);
      adoptPendingTurnIfNeeded();
      if (replacingPendingTurn) enterThinkingInterruptionCheck(ownedThinkingGeneration);
      liveUtteranceGuard.start();
      voiceLatencyRef.current.beginIfNeeded();
      voiceLatencyRef.current.mark('speech_started');
      traceVoiceLifecycle('speech_started');
    };

    recognition.onspeechend = () => {
      if (!isCurrentRecognitionOwner()) return;
      setOrbSpeechActive(false);
      liveUtteranceGuard.finish();
      voiceLatencyRef.current.mark('speech_ended');
      if (latestLiveCandidate) {
        scheduleRecognizedTurnFinalization(
          voiceTurnFinalizationDelay(latestLiveCandidate, { recognitionBoundary: true }),
        );
      }
    };

    recognition.onresult = async (event) => {
      console.timeStamp('[VOICE] recognition result');
      if (!isCurrentRecognitionOwner() || isMutedRef.current) {
        traceVoiceLifecycle('stale_callback_ignored', { callback: 'recognition_result' });
        return;
      }
      adoptPendingTurnIfNeeded();

      const snapshot = recognitionResultSnapshot(event);
      if (!snapshot.candidate) return;
      // Recognized content is the fallback speech-active signal on engines
      // that omit speechstart for continuous/interruption recognition.
      setOrbSpeechActive(true);
      if (replacingPendingTurn) enterThinkingInterruptionCheck(ownedThinkingGeneration);
      traceVoiceLifecycle(snapshot.hasFinal ? 'transcript_final' : 'transcript_partial');
      liveFinalBufferRef.current = snapshot.finalCandidate;
      hasUnsettledInterim = snapshot.hasInterim;
      thinkingCandidateConfidence = snapshot.confidence;
      const merged = snapshot.candidate;
      if (replacingPendingTurn && snapshot.hasFinal && merged) {
        thinkingCandidate = snapshot.finalCandidate;
      }
      latestLiveCandidate = merged;
      setLiveTranscript(merged);
      if (snapshot.hasFinal) scheduleFinalTranscriptRecovery();
      if (replacingPendingTurn && snapshot.hasFinal && !snapshot.hasInterim) {
        // A final transcript has already reached a phrase boundary. Classify
        // it immediately while the pending request still belongs to this
        // recognizer; waiting through the ordinary dictation pause can let the
        // obsolete response take the microphone and discard the interruption.
        void finalizeRecognizedTurn(
          isControlOnlyVoiceCommand(merged) ? 'control' : 'final',
        );
        return;
      }
      // Some Web Speech implementations omit speechstart, speechend, or final
      // events. Any recognized content starts the absolute guard and re-arms a
      // bounded quiet-window finalizer. Final results use the shared adaptive
      // pause policy; interim-only streams get a longer stall window.
      liveUtteranceGuard.start();
      scheduleRecognizedTurnFinalization(
        snapshot.hasFinal && !snapshot.hasInterim
          ? voiceTurnFinalizationDelay(merged, { recognitionBoundary: true })
          : LIVE_INTERIM_STALL_MS,
      );
    };

    recognition.onerror = (event) => {
      setIsRecording(false);
      liveUtteranceGuard.finish();
      if (recognitionRef.current === recognition) setOrbSpeechActive(false);

      const err = event?.error || '';
      traceVoiceLifecycleIncident('recognition_error', {
        recognition_state: 'error',
        interruption_mode: whileThinking ? 'thinking' : 'listening',
        invalidation_reason: ['aborted', 'no-speech', 'network', 'audio-capture'].includes(err)
          ? err.replace('-', '_')
          : 'other',
      });
      if (err === 'aborted' || (err === 'no-speech' && isLiveOpenRef.current && !isMutedRef.current)) {
        return;
      }

      // Safari can expose webkitSpeechRecognition while its remote recognition
      // service is unavailable. Fall back to first-party MediaRecorder capture
      // for service/network failures; permission denials remain explicit and
      // are never bypassed.
      if (
        ['network', 'service-not-allowed', 'language-not-supported', 'audio-capture'].includes(err) &&
        typeof window.MediaRecorder === 'function' &&
        isLiveOpenRef.current &&
        !isMutedRef.current
      ) {
        setLiveVoiceState('transcribing');
        liveVoiceStateRef.current = 'transcribing';
        stopRecognition();
        startRecordedRecognition();
        return;
      }

      setLiveVoiceState('error');
      liveVoiceStateRef.current = 'error';
      setError('Live voice input failed.');
    };

    recognition.onend = () => {
      traceVoiceLifecycleIncident('recognition_ended', {
        recognition_state: 'ended',
        interruption_mode: whileThinking ? 'thinking' : 'listening',
      });
      setIsRecording(false);
      liveUtteranceGuard.finish();

      // stopRecognition() deliberately clears ownership before an old live
      // listening recognizer's asynchronous onend arrives. Never let that
      // stale session schedule a replacement over the barge-in recognizer
      // that now owns the microphone during playback.
      if (recognitionRef.current !== recognition) return;
      if (!isCurrentRecognitionOwner()) return;
      setOrbSpeechActive(false);

      if (!isLiveOpenRef.current) {
        setLiveVoiceState('idle');
        liveVoiceStateRef.current = 'idle';
        return;
      }

      if (isMutedRef.current) {
        setLiveVoiceState('muted');
        liveVoiceStateRef.current = 'muted';
        return;
      }

      // A recognition engine ending is a strong phrase boundary. Settle the
      // owned stable snapshot through the same confidence/echo filters rather
      // than clearing visible words or waiting for a callback that can no
      // longer arrive.
      if (latestLiveCandidate && !liveFinalizationClaimed) {
        void finalizeRecognizedTurn('recognition_end');
        return;
      }

      if (
        liveVoiceStateRef.current === 'speaking' ||
        (liveVoiceStateRef.current === 'thinking' && !whileThinking) ||
        liveVoiceStateRef.current === 'transcribing'
      ) {
        return;
      }

      setTimeout(() => {
        if (
          recognitionRef.current === recognition &&
          isLiveOpenRef.current &&
          !isMutedRef.current &&
          liveVoiceStateRef.current !== 'speaking' &&
          (liveVoiceStateRef.current !== 'thinking' || whileThinking) &&
          (!whileThinking || isCurrentVoiceGeneration(
            liveVoiceGenerationRef,
            generation,
          ))
        ) {
          startLiveRecognition({ whileThinking, generation });
        }
      }, 140);
    };

    try {
      recognition.start();
    } catch (_) {
      console.error('[ChatShell] Live recognition error');
      setLiveVoiceState('error');
      liveVoiceStateRef.current = 'error';
      setError('Live voice could not start.');
    }
  };

  const openLiveVoice = async () => {
    setError('');
    voiceLatencyRef.current.cancel();
    voiceLifecycleRef.current = createVoiceIncidentRef();
    voiceThreadOwnerRef.current = createVoiceIncidentRef();
    voiceMicrophoneEverAcquiredRef.current = false;
    voicePreviousThreadRef.current = activeThreadIdRef.current || '';
    voiceSessionIdRef.current = genClientMessageId();
    voiceSessionOpeningRef.current = { microphoneReady: false };
    voiceLatencyRef.current.begin();
    traceVoiceLifecycle('voice_session_opened');
    traceVoiceLifecycleIncident('voice_session_opened', {
      microphone_state: 'unavailable',
      owner_valid: true,
    });
    // Only a deliberate user retry may request microphone permission again.
    // Recognition restarts fail closed after denial instead of prompt-looping.
    micPermissionDeniedRef.current = false;

    // This must remain synchronous inside the button gesture. Safari otherwise
    // rejects server audio that arrives after the chat request completes.
    if (!primeBrightResolveAudio()) {
      traceVoiceLifecycleIncident('voice_session_closed', {
        transition_result: 'audio_unavailable',
        owner_valid: false,
      });
      voiceLatencyRef.current.cancel();
      voiceSessionOpeningRef.current = null;
      voiceSessionIdRef.current = '';
      voiceLifecycleRef.current = '';
      voiceThreadOwnerRef.current = '';
      setError('Voice playback is not available in this browser.');
      return;
    }

    try {
      const unlocked = await unlockBrightResolveAudio();
      if (!unlocked) {
        // Continue with the persistent HTMLAudioElement primed above. This is
        // the reliable Safari path when Web Audio cannot stay unlocked.
        console.warn('[ChatShell] Web Audio unavailable; using HTML audio fallback.');
      }
    } catch (error) {
      console.warn('[ChatShell] Audio unlock failed:', error);
    }

    // Unlock browser speech playback during the user's button click.
    // Safari/WebKit may block speech started only after an async API response.
    try {
      if (
        typeof window !== 'undefined' &&
        window.speechSynthesis &&
        typeof window.SpeechSynthesisUtterance !== 'undefined'
      ) {
        window.speechSynthesis.cancel();
        window.speechSynthesis.getVoices();



        const unlockUtterance = new window.SpeechSynthesisUtterance(' ');
        unlockUtterance.volume = 0;
        window.speechSynthesis.speak(unlockUtterance);
      }
    } catch (error) {
      console.warn('[ChatShell] Speech unlock failed:', error);
    }

    const openingThreadId = activeThreadIdRef.current;
    const readiness = await requestVoiceCanaryReadiness(openingThreadId);
    if (readiness.kind === 'blocked') {
      voiceLatencyRef.current.cancel();
      voiceSessionOpeningRef.current = null;
      voiceSessionIdRef.current = '';
      voiceLifecycleRef.current = '';
      voiceThreadOwnerRef.current = '';
      voiceCanaryBindingRef.current = null;
      setError('Live Voice cannot start until the assigned canary conversation is open.');
      return;
    }
    if (activeThreadIdRef.current !== openingThreadId) {
      voiceLatencyRef.current.cancel();
      voiceSessionOpeningRef.current = null;
      voiceSessionIdRef.current = '';
      voiceLifecycleRef.current = '';
      voiceThreadOwnerRef.current = '';
      voiceCanaryBindingRef.current = null;
      setError('The conversation changed while Live Voice was opening. Please try again.');
      return;
    }
    voiceCanaryBindingRef.current = readiness.kind === 'bound'
      ? readiness.binding
      : null;

    abortVoiceTurn(liveRequestAbortRef);
    advanceVoiceGeneration(liveVoiceGenerationRef);
    bargeInOwnerRef.current = null;
    thinkingInterruptionOwnerRef.current = null;
    liveProgressWatchdogRef.current?.clear?.();
    pendingLiveUserTranscriptRef.current = '';
    setIsLiveOpen(true);
    isLiveOpenRef.current = true;
    liveInputSuspendedRef.current = false;
    setIsMuted(false);
    isMutedRef.current = false;
    setLiveTranscript('');
    setAssistantPreview('');
    setLiveVoiceState('listening');
    liveVoiceStateRef.current = 'listening';
    await startLiveRecognition();
  };

  const closeLiveVoice = () => {
    const closingGeneration = liveVoiceGenerationRef.current;
    traceVoiceLifecycleIncident('voice_session_closed', {
      microphone_state: mediaStreamRef.current ? 'active' : 'released',
      owner_valid: true,
    });
    isLiveOpenRef.current = false;
    liveInputSuspendedRef.current = false;
    abortVoiceTurn(liveRequestAbortRef);
    advanceVoiceGeneration(liveVoiceGenerationRef);
    voiceSessionIdRef.current = '';
    voiceCanaryBindingRef.current = null;
    voiceSessionOpeningRef.current = null;
    bargeInOwnerRef.current = null;
    thinkingInterruptionOwnerRef.current = null;
    voiceLatencyRef.current.cancel();
    liveProgressWatchdogRef.current?.clear?.();
    pendingLiveUserTranscriptRef.current = '';
    if (liveSilenceTimerRef.current) clearTimeout(liveSilenceTimerRef.current);
    if (bargeInDecisionTimerRef.current) clearTimeout(bargeInDecisionTimerRef.current);
    bargeInDecisionTimerRef.current = null;
    bargeInPausedRef.current = false;
    bargeInCycleOpenRef.current = false;
    bargeInSpeechActiveRef.current = false;
    setOrbSpeechActive(false);
    bargeInIgnoreResultsUntilRef.current = 0;
    liveSilenceTimerRef.current = null;
    liveFinalBufferRef.current = '';
    voiceObservationQueueRef.current.clear();
    stopSpeaking();
    releaseLiveInputAudio();
    setIsLiveOpen(false);
    setLiveTranscript('');
    setAssistantPreview('');
    setLiveVoiceState('idle');
    liveVoiceStateRef.current = 'idle';
    voiceLifecycleRef.current = '';
    voiceThreadOwnerRef.current = '';
    voiceMicrophoneEverAcquiredRef.current = false;
    voicePreviousThreadRef.current = '';
    if (livePendingGenerationRef.current === closingGeneration) {
      livePendingGenerationRef.current = null;
      setIsSending(false);
    }
    window.setTimeout(() => scrollConversationToBottom('smooth'), 0);
  };

  const toggleMute = () => {
    const next = !isMutedRef.current;
    setIsMuted(next);
    isMutedRef.current = next;

      if (next) {
      if (liveSilenceTimerRef.current) clearTimeout(liveSilenceTimerRef.current);
      if (bargeInDecisionTimerRef.current) clearTimeout(bargeInDecisionTimerRef.current);
      stopImmediateBargeInActivityMonitor();
      bargeInDecisionTimerRef.current = null;
        bargeInPausedRef.current = false;
        bargeInOwnerRef.current = null;
        thinkingInterruptionOwnerRef.current = null;
      bargeInCycleOpenRef.current = false;
      bargeInSpeechActiveRef.current = false;
      setOrbSpeechActive(false);
      bargeInIgnoreResultsUntilRef.current = 0;
      liveSilenceTimerRef.current = null;
      stopRecognition();
      releaseLiveInputAudio();
      if (!['speaking', 'thinking', 'transcribing'].includes(liveVoiceStateRef.current)) {
        setLiveVoiceState('muted');
        liveVoiceStateRef.current = 'muted';
      }
      publishVoiceInputEnergy(0);
        } else if (isLiveOpenRef.current) {
          if (liveVoiceStateRef.current === 'speaking') {
            if (getDuplexVoiceMonitoringMode()) {
              startAudioMeter();
              startVoiceBargeInMonitoring(recentAssistantSpeechRef.current.text);
            }
      } else if (liveVoiceStateRef.current === 'thinking') {
        startLiveRecognition({
          whileThinking: true,
          generation: livePendingGenerationRef.current,
        });
      } else {
        setLiveVoiceState('listening');
        liveVoiceStateRef.current = 'listening';
        restartLiveRecognition();
      }
    }
  };

  useEffect(() => {
    const suspendLiveInput = () => {
      if (!isLiveOpenRef.current || liveInputSuspendedRef.current) return;
      traceVoiceLifecycleIncident('page_hidden', {
        microphone_state: mediaStreamRef.current ? 'active' : 'released',
      });
      liveInputSuspendedRef.current = true;
      stopImmediateBargeInActivityMonitor();
      releaseLiveInputAudio();
      publishVoiceInputEnergy(0);
    };

    const resumeLiveInput = () => {
      if (
        !liveInputSuspendedRef.current ||
        !isLiveOpenRef.current ||
        isMutedRef.current ||
        (typeof document !== 'undefined' && document.visibilityState === 'hidden')
      ) return;
      traceVoiceLifecycleIncident('page_visible', {
        microphone_state: mediaStreamRef.current ? 'active' : 'released',
      });
      liveInputSuspendedRef.current = false;
      if (liveVoiceStateRef.current === 'speaking') {
        if (getDuplexVoiceMonitoringMode()) {
          startAudioMeter();
          startVoiceBargeInMonitoring(recentAssistantSpeechRef.current.text);
        }
      } else if (liveVoiceStateRef.current === 'thinking') {
        startLiveRecognition({
          whileThinking: true,
          generation: livePendingGenerationRef.current,
        });
      } else {
        setLiveVoiceState('listening');
        liveVoiceStateRef.current = 'listening';
        restartLiveRecognition();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') suspendLiveInput();
      else resumeLiveInput();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', suspendLiveInput);
    window.addEventListener('pageshow', resumeLiveInput);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', suspendLiveInput);
      window.removeEventListener('pageshow', resumeLiveInput);
    };
  }, []);

  return (
    <div
      className="br-chat-page relative flex h-[100dvh] items-center justify-center overflow-hidden px-2 pb-2 pt-[72px] text-white sm:px-4 sm:pb-4 sm:pt-[80px]"
    >
      <div className="br-fixed-background" style={{ backgroundImage: "url('/background-stars.png')" }} aria-hidden="true" />
      <div className={`br-chat-shell${isSending ? ' br-chat-shell--responding' : ''} relative z-10 mx-auto flex h-[min(46rem,calc(100dvh-5rem))] min-h-0 w-full max-w-5xl overflow-hidden rounded-2xl border border-white/20 bg-white/10 text-white shadow-2xl backdrop-blur-md sm:h-[min(46rem,calc(100dvh-6rem))] sm:rounded-3xl`} data-testid="public-chat-surface">
        {isConversationPanelOpen && <div className="absolute inset-0 z-40 bg-black/55" onClick={() => setIsConversationPanelOpen(false)}><div className="h-full w-[min(20rem,88vw)]" onClick={(event) => event.stopPropagation()}><ConversationSidebar threads={threads} archivedThreads={archivedThreads} activeThreadId={activeThreadId} loading={isThreadLoading} onNew={newThread} onSelect={(thread) => { openThread(thread); setIsConversationPanelOpen(false); }} onDelete={deleteThread} onRestore={restoreThread} onPermanentDelete={permanentlyDeleteThread} onClose={() => setIsConversationPanelOpen(false)} /></div></div>}
        <div className="br-chat-main flex min-h-0 min-w-0 flex-1 flex-col gap-3 p-3 sm:gap-4 sm:p-6">
          <div className="br-chat-toolbar flex min-w-0 shrink-0 items-center gap-2 border-b border-white/10 pb-3">
            <button type="button" onClick={() => setIsConversationPanelOpen(true)} aria-label="Open conversations" title="Conversations" className="inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5 px-2.5 text-white/75 transition hover:bg-white/10 hover:text-white"><svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-2"><path d="M4 6h16M4 12h16M4 18h16" /></svg></button>
            <button
              type="button"
              onClick={newThread}
              disabled={isThreadLoading}
              aria-label="Start a new chat"
              title="New chat"
              className="inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5 px-2.5 text-white/75 transition hover:bg-white/10 hover:text-white disabled:opacity-50"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-2"><path d="M12 5v14M5 12h14" /></svg>
              <span className="ml-1.5 hidden text-xs font-medium sm:inline">New chat</span>
            </button>
            <p className="br-chat-title min-w-0 flex-1 truncate px-1 text-sm font-medium text-white/80">{threads.find((thread) => thread.id === activeThreadId)?.title || 'New conversation'}</p>
          </div>
          {isHistorySavingEnabled === false && (
            <p className="shrink-0 rounded-lg border border-amber-100/15 bg-amber-100/8 px-3 py-2 text-xs leading-relaxed text-amber-50/75" role="status">
              Conversation history is off, so new messages will not return after you leave.{' '}
              <a className="font-semibold text-amber-50 underline decoration-amber-100/35 underline-offset-2" href="/settings#conversation">Turn on history in Settings</a>.
            </p>
          )}
          {messages.length === 0 && <div className="shrink-0"><DisclaimerBanner /></div>}

          <TranscriptPanel
            messages={messages}
            messagesEndRef={messagesEndRef}
            transcriptRef={transcriptRef}
            onTranscriptScroll={handleTranscriptScroll}
            playingMessageId={playingMessageId}
            onPlayMessage={handlePlayMessage}
            onStopMessage={handleStopMessage}
            onEditMessage={startEditingMessage}
            isResponding={isSending}
          />

          {error && <p className="shrink-0 text-center font-semibold text-red-400">{error}</p>}

          <TextComposer
            input={input}
            setInput={setInput}
            isSending={isSending}
            isThreadLoading={isThreadLoading}
            onSubmit={handleSend}
            onOpenTranscribe={openTranscribe}
            onOpenLiveVoice={openLiveVoice}
            isEditing={Boolean(editingMessageId)}
            onCancelEdit={cancelEditingMessage}
          />
        </div>
      </div>

      {isTranscribeOpen && (
        <VoiceDraftPanel
          isRecording={isRecording}
          onCancel={closeTranscribe}
          onFinish={finishTranscribe}
        />
      )}

      {isLiveOpen && (
        <LiveVoicePanel
          liveVoiceState={liveVoiceState}
          isMuted={isMuted}
          ccEnabled={ccEnabled}
          liveTranscript={liveTranscript}
          assistantPreview={assistantPreview}
          onToggleMute={toggleMute}
          onToggleCC={() => setCCEnabled((prev) => !prev)}
          onInterrupt={interruptLiveVoice}
          onClose={closeLiveVoice}
          onThinkingVisible={markThinkingVisible}
          speechActive={orbSpeechActive}
        />
      )}
    </div>
  );
}
